import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
   Box,
   Container,
   Typography,
   Card,
   CardContent,
   Avatar,
   Button,
   Chip,
   Stack,
   Divider,
   IconButton,
   Paper,
   Snackbar,
   Alert,
   CircularProgress,
   Fade,
   Skeleton,
   Dialog,
   DialogTitle,
   DialogContent,
   DialogActions,
   Tooltip,
   useTheme,
   TextField,
} from '@mui/material';
import {
   PhotoLibrary as PhotoLibraryIcon,
   QrCodeScanner as QrCodeScannerIcon,
   Link as LinkIcon,
   CheckCircle as CheckCircleIcon,
   Cancel as CancelIcon,
   AccessTime as AccessTimeIcon,
   Event as EventIcon,
   PhotoAlbum as PhotoAlbumIcon,
   Add as AddIcon,
   Check as CheckIcon,
   TrendingUp as TrendingUpIcon,
   PersonAdd as PersonAddIcon,
   CalendarToday as CalendarTodayIcon,
   OpenInNew as OpenInNewIcon,
   Photo as PhotoIcon,
   People as PeopleIcon,
   Lock as LockIcon,
   Public as PublicIcon,
   History as HistoryIcon,
   Refresh as RefreshIcon,
   PhotoLibrary as PhotoLibraryStatIcon,
   Phone as PhoneIcon,
   Language as LanguageIcon,
   Business as BusinessIcon,
   Close as CloseIcon,
   Message as MessageIcon,
   Send as SendIcon,
   ArrowBack as BackIconMsg,
   CloudUpload as CloudUploadIcon,
} from '@mui/icons-material';
import { useUser } from '../../contexts/UserContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import {
   collection,
   query,
   where,
   onSnapshot,
   getDocs,
   Timestamp,
   doc,
   getDoc,
   limit,
   addDoc,
   orderBy,
   serverTimestamp,
} from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
   AlbumAccessRequest,
   approveAlbumAccessRequest,
   denyAlbumAccessRequest,
} from '../../services/feedService';
import { getAuthHeaders } from '../../utils/auth';
import SubscriptionGuard from '../../components/SubscriptionGuard';
import AlbumInvitationSentCard, {
   AlbumInvitationSent,
} from '../../components/albums/AlbumInvitationSentCard';
import AlbumInvitationReceivedCard, {
   AlbumInvitationReceived,
} from '../../components/albums/AlbumInvitationReceivedCard';
import AlbumLinkClickCard, { AlbumLinkClick } from '../../components/albums/AlbumLinkClickCard';
import AlbumDownloadCard, { AlbumDownload } from '../../components/albums/AlbumDownloadCard';
import AlbumCreatedCard, { AlbumCreated } from '../../components/albums/AlbumCreatedCard';
import CreateAlbumDialog from '../../components/albums/CreateAlbumDialog';
import EventCreationDialog from '../../components/events/EventCreationDialog';
import EventCreatedCard, { EventCreated } from '../../components/events/EventCreatedCard';
import ProfileUpdateCard from '../../components/profile/ProfileUpdateCard';
import { ProfileCompletionStatus } from '../../components/profile/ProfileUpdateCard';
import { checkProfileCompletion } from '../../utils/profileCompletion';
import EditProfileDialog from '../Profile/components/EditProfileDialog';
import ParticipantProfileModal from '../../components/ParticipantProfileModal';
import { formatDistanceToNow } from 'date-fns';
import PhotographerFeedCard, {
   PhotographerFeedCardData,
} from '../../components/feed/PhotographerFeedCard';
import InlinePhotoScroller from '../../components/feed/InlinePhotoScroller';
import { PhotographerBadge } from '../../components/common/PhotographerBadge';
import RegisterPhotographerForm from '../../components/photographer/RegisterPhotographerForm';
import SponsorCard, { SponsorCardData } from '../../components/feed/SponsorCard';

type ActivityItem = {
   id: string;
   action: string;
   albumId?: string;
   albumTitle?: string;
   details?: string;
   contextId?: string;
   createdAt?: Timestamp | null;
   userId?: string;
   actorId?: string;
   resolvedActorId?: string;
   targetUserId?: string;
   targetUserDisplayName?: string;
   targetUserPhotoURL?: string;
};

interface EnrichedAlbumData {
   coverPhoto?: string;
   photoCount: number;
   title: string;
   photos: string[]; // Array of photo URLs for stacking
}

interface EnrichedUserData {
   displayName: string;
   photoURL?: string;
   email?: string;
}

function feedCardTimestampMillis(value: any): number {
   if (!value) return 0;
   if (value instanceof Date) return value.getTime();
   if (typeof value.toDate === 'function') return value.toDate().getTime();
   if (typeof value.seconds === 'number') return value.seconds * 1000;
   return 0;
}

/** Feed row for albums the user can access but has no `album_created` doc (legacy / backfill gap). */
function albumDocToSyntheticFeedCard(
   album: any,
   currentUserId: string,
   creator: EnrichedUserData | null,
): AlbumCreated {
   const typeRaw = album.type || 'private';
   const albumType =
      typeRaw === 'public' || typeRaw === 'paid' || typeRaw === 'private' ? typeRaw : 'private';
   const isPublic = album.isPublic === true || albumType === 'public';
   let invitationMemberRole: string | undefined;
   const m = album.members?.find((x: any) => x.userId === currentUserId);
   if (m?.role) invitationMemberRole = String(m.role);
   else if (Array.isArray(album.memberIds) && album.memberIds.includes(currentUserId)) {
      invitationMemberRole = 'viewer';
   }
   const createdBy = album.createdBy || album.owner || '';
   return {
      id: `membership-${album.id}`,
      card_type: 'album_created',
      userid: currentUserId,
      albumId: album.id,
      albumTitle: album.title || 'Untitled Album',
      albumDescription: album.description || '',
      albumCoverUrl: album.coverPhotoUrl || album.coverPhoto || '',
      albumPhotoCount:
         typeof album.photoCount === 'number' ? album.photoCount : (album.photoIds?.length ?? 0),
      albumType,
      albumIsPublic: isPublic,
      albumDate: album.albumDate || '',
      albumPhotos: [],
      creatorId: createdBy,
      creatorName: creator?.displayName || 'Unknown',
      creatorPhoto: creator?.photoURL || '',
      creatorEmail: creator?.email || '',
      createdAt: album.createdAt ?? album.updatedAt ?? null,
      seen: true,
      fromInvitationAccept: false,
      invitationMemberRole,
      accessGrantedVia: undefined,
   };
}

interface AccessibleAlbum {
   id: string;
   title: string;
   description?: string;
   type: 'private' | 'public' | 'paid';
   coverPhotoUrl?: string;
   photoCount: number;
   owner: string;
   ownerName?: string;
   ownerPhoto?: string;
   createdAt: any;
   accessGrantedAt?: any;
   eventId?: string;
   eventTitle?: string;
   eventDate?: any;
   photos?: string[];
   groupCount?: number;
   feedMemberRole?: 'owner' | 'editor' | 'viewer' | null;
   enableParticipantUpload?: boolean;
   allowPhotographerUpload?: boolean;
}

const Feed: React.FC = () => {
   const theme = useTheme();
   const { currentUser } = useUser();
   const navigate = useNavigate();
   const [searchParams, setSearchParams] = useSearchParams();
   const {
      checkSubscriptionStatus,
      setSubscriptionModalOpen,
      currentSubscription,
      hasSubscriptions,
   } = useSubscription();
   const [accessRequests, setAccessRequests] = useState<AlbumAccessRequest[]>([]);
   const [accessibleAlbums, setAccessibleAlbums] = useState<AccessibleAlbum[]>([]);
   const [sentInvitations, setSentInvitations] = useState<AlbumInvitationSent[]>([]);
   const [receivedInvitations, setReceivedInvitations] = useState<AlbumInvitationReceived[]>([]);
   const [albumLinkClicks, setAlbumLinkClicks] = useState<AlbumLinkClick[]>([]);
   const [albumDownloads, setAlbumDownloads] = useState<AlbumDownload[]>([]);
   const [albumsCreated, setAlbumsCreated] = useState<AlbumCreated[]>([]);
   const [membershipAlbumsForFeed, setMembershipAlbumsForFeed] = useState<{
      albums: any[];
      creators: Record<string, EnrichedUserData | null>;
   } | null>(null);
   const [eventsCreated, setEventsCreated] = useState<EventCreated[]>([]);
   const [loading, setLoading] = useState(true);
   const [loadingAlbums, setLoadingAlbums] = useState(true);
   const [loadingSentInvitations, setLoadingSentInvitations] = useState(true);
   const [loadingReceivedInvitations, setLoadingReceivedInvitations] = useState(true);
   const [loadingLinkClicks, setLoadingLinkClicks] = useState(true);
   const [loadingDownloads, setLoadingDownloads] = useState(true);
   const [eventDialogOpen, setEventDialogOpen] = useState(false);
   const [albumDialogOpen, setAlbumDialogOpen] = useState(false);
   const [, setAlbumCreating] = useState(false);
   const [recentActivities, setRecentActivities] = useState<
      {
         id: string;
         action: string;
         albumId?: string;
         albumTitle?: string;
         details?: string;
         contextId?: string;
         createdAt?: Timestamp | null;
         userId?: string;
         actorId?: string;
         resolvedActorId?: string;
         targetUserId?: string;
         targetUserDisplayName?: string;
         targetUserPhotoURL?: string;
      }[]
   >([]);
   const [activityUsers, setActivityUsers] = useState<
      Record<string, { displayName: string; photoURL: string }>
   >({});
   const [activityAlbumCovers, setActivityAlbumCovers] = useState<Record<string, string>>({});
   const [loadingRecentActivities, setLoadingRecentActivities] = useState(true);

   /** loading = show CTA until Firestore confirms photographer/org */
   const [photographerEligibility, setPhotographerEligibility] = useState<
      'loading' | 'eligible' | 'ineligible'
   >('loading');
   const [photographerEligibilityRefreshKey, setPhotographerEligibilityRefreshKey] = useState(0);
   const [photographerSignupDialogOpen, setPhotographerSignupDialogOpen] = useState(false);
   const [photographerSignupDialogSuccess, setPhotographerSignupDialogSuccess] = useState(false);
   const [photographerSignupSubmitting, setPhotographerSignupSubmitting] = useState(false);

   // Photographer feed cards state (aggregated across accessible albums)
   const [photographerCards, setPhotographerCards] = useState<PhotographerFeedCardData[]>([]);

   // Photographer profile popover (reuse US-05 popup behavior)
   const [profilePopoverUserId, setProfilePopoverUserId] = useState<string | null>(null);
   const [profilePopoverData, setProfilePopoverData] = useState<{
      displayName?: string;
      photoURL?: string;
      accountType?: string[];
      photographerProfile?: any | null;
      portfolioPhotos?: { id: string; url: string; thumbnailUrl?: string; title?: string }[];
   } | null>(null);
   const [profilePopoverLoading, setProfilePopoverLoading] = useState(false);
   const [profilePopoverMessageMode, setProfilePopoverMessageMode] = useState(false);
   const [profilePopoverMessageText, setProfilePopoverMessageText] = useState('');
   const [profilePopoverSenderEmail, setProfilePopoverSenderEmail] = useState('');
   const [profilePopoverSenderName, setProfilePopoverSenderName] = useState('');
   const [profilePopoverSending, setProfilePopoverSending] = useState(false);
   const [profilePopoverMessageError, setProfilePopoverMessageError] = useState<string | null>(
      null,
   );

   // Simple lightbox for photographer photos on feed
   const [lightboxOpen, setLightboxOpen] = useState(false);
   const [lightboxUrls, setLightboxUrls] = useState<string[]>([]);
   const [lightboxIndex, setLightboxIndex] = useState(0);

   // Refresh trigger for accessible albums (incremented when invitation is accepted)
   const [albumsRefreshTrigger, setAlbumsRefreshTrigger] = useState(0);

   /** Main feed vertical list: first batch and each "load more" */
   const FEED_CARDS_INITIAL = 5;
   const FEED_CARDS_PAGE = 5;

   // Accessible albums pagination
   const ALBUM_PAGE_SIZE = 10;
   const [allAlbumIds, setAllAlbumIds] = useState<string[]>([]);
   const [hasMoreAlbums, setHasMoreAlbums] = useState(false);
   const [loadingMoreAlbums, setLoadingMoreAlbums] = useState(false);
   const albumSentinelRef = useRef<HTMLDivElement | null>(null);

   const [uploadingAlbumId, setUploadingAlbumId] = useState<string | null>(null);
   const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});

   const [sponsorCards, setSponsorCards] = useState<SponsorCardData[]>([]);

   const [activeFilter, setActiveFilter] = useState<
      'all' | 'photos' | 'analytics' | 'notifications' | 'hot'
   >(
      () =>
         (sessionStorage.getItem('feedActiveFilter') as
            | 'all'
            | 'photos'
            | 'analytics'
            | 'notifications'
            | 'hot') || 'all',
   );

   useEffect(() => {
      sessionStorage.setItem('feedActiveFilter', activeFilter);
   }, [activeFilter]);

   const [visibleFeedCardCount, setVisibleFeedCardCount] = useState(FEED_CARDS_INITIAL);
   const feedLoadMoreSentinelRef = useRef<HTMLDivElement | null>(null);

   useEffect(() => {
      setVisibleFeedCardCount(FEED_CARDS_INITIAL);
   }, [activeFilter]);

   const [filterBarVisible, setFilterBarVisible] = useState(true);
   const lastScrollY = useRef(0);

   useEffect(() => {
      const handleScroll = () => {
         const currentY = window.scrollY;
         if (Math.abs(currentY - lastScrollY.current) < 10) return;
         setFilterBarVisible(currentY < lastScrollY.current || currentY < 100);
         lastScrollY.current = currentY;
      };
      window.addEventListener('scroll', handleScroll, { passive: true });
      return () => window.removeEventListener('scroll', handleScroll);
   }, []);

   useEffect(() => {
      if (!currentUser?.uid) return;
      getAuthHeaders().then((headers) => {
         fetch(
            `${process.env.REACT_APP_API_URL}/api/sponsors/cards/active?userId=${currentUser.uid}`,
            { headers },
         )
            .then((r) => r.json())
            .then((cards) => {
               if (Array.isArray(cards)) setSponsorCards(cards);
            })
            .catch(() => {});
      });
   }, [currentUser?.uid]);

   // Top albums state (for sidebar - albums user is member of or invited to)
   interface TopAlbum {
      id: string;
      title: string;
      photoCount: number;
      coverUrl?: string;
      status: 'member' | 'invited';
   }
   const [topAlbums, setTopAlbums] = useState<TopAlbum[]>([]);
   const [loadingTopAlbums, setLoadingTopAlbums] = useState(true);

   // Quick stats state
   interface QuickStats {
      totalPhotos: number;
      albumInvites: number;
      eventsCount: number;
   }
   const [quickStats, setQuickStats] = useState<QuickStats>({
      totalPhotos: 0,
      albumInvites: 0,
      eventsCount: 0,
   });
   const [loadingQuickStats, setLoadingQuickStats] = useState(true);

   // Store enriched data for albums and users
   const [albumDetails, setAlbumDetails] = useState<Record<string, EnrichedAlbumData>>({});
   const [userDetails, setUserDetails] = useState<Record<string, EnrichedUserData>>({});

   // Loading states for approve/reject actions
   const [approvingUsers, setApprovingUsers] = useState<Set<string>>(new Set());
   const [rejectingUsers, setRejectingUsers] = useState<Set<string>>(new Set());

   const photographerWebsite =
      process.env.REACT_APP_PHOTOGRAPHER_WEBSITE || process.env.PHOTOGRAPHER_WEBSITE || '';

   // Snackbar state
   const [snackbarOpen, setSnackbarOpen] = useState(false);
   const [snackbarMessage, setSnackbarMessage] = useState('');
   const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'warning'>(
      'success',
   );

   // Profile completion state
   const [profileStatus, setProfileStatus] = useState<ProfileCompletionStatus | null>(null);
   const [loadingProfileStatus, setLoadingProfileStatus] = useState(true);
   const [editProfileDialogOpen, setEditProfileDialogOpen] = useState(false);
   const [profileData, setProfileData] = useState<any>({});

   // Activity popup state (album details vs user info)
   const [activityPopupType, setActivityPopupType] = useState<'album' | 'user' | null>(null);
   const [selectedActivity, setSelectedActivity] = useState<ActivityItem | null>(null);
   const [selectedUserForPopup, setSelectedUserForPopup] = useState<any>(null);
   const [loadingActivityUser, setLoadingActivityUser] = useState(false);

   // Map album id -> cover URL for activity thumbnails (from accessible albums + activity data)
   const albumIdToCover = useMemo(() => {
      const m: Record<string, string> = {};
      accessibleAlbums.forEach((a) => {
         if (a.coverPhotoUrl) m[a.id] = a.coverPhotoUrl;
      });
      Object.entries(activityAlbumCovers).forEach(([id, url]) => {
         if (url && !m[id]) m[id] = url;
      });
      return m;
   }, [accessibleAlbums, activityAlbumCovers]);

   // Check for pending album access after payment redirect
   useEffect(() => {
      const checkPendingAlbumPayment = () => {
         const pendingAccess = localStorage.getItem('pendingAlbumAccess');
         if (pendingAccess) {
            try {
               const accessData = JSON.parse(pendingAccess);
               // Check if not too old (within 5 minutes)
               if (Date.now() - accessData.timestamp < 300000) {
                  // Generic success message - feed card will show specific status
                  setSnackbarMessage(
                     `Payment successful for "${accessData.albumTitle}"! Check your feed for updates.`,
                  );
                  setSnackbarSeverity('success');
                  setSnackbarOpen(true);
               }
               // Clear the pending access data
               localStorage.removeItem('pendingAlbumAccess');
            } catch (error) {
               console.error('Error parsing pending album access:', error);
               localStorage.removeItem('pendingAlbumAccess');
            }
         }
      };

      checkPendingAlbumPayment();
   }, []);

   // Show Snackbar when redirected from album access request, then clear query
   useEffect(() => {
      if (searchParams.get('from') === 'album_request') {
         setSnackbarMessage(
            "Your access request was sent. You'll see updates here when the album owner responds.",
         );
         setSnackbarSeverity('success');
         setSnackbarOpen(true);
         setSearchParams({}, { replace: true });
      }
   }, [searchParams, setSearchParams]);

   // Helper function to check if user has created any events
   const hasCreatedAnyEvents = async (): Promise<boolean> => {
      if (!currentUser?.uid) return false;
      try {
         const eventsQuery = query(
            collection(db, 'events'),
            where('createdBy', '==', currentUser.uid),
            limit(1),
         );
         const eventsSnapshot = await getDocs(eventsQuery);
         return eventsSnapshot.docs.length > 0;
      } catch (error) {
         return false;
      }
   };

   // Helper function to check if user has created any albums
   const hasCreatedAnyAlbums = async (): Promise<boolean> => {
      if (!currentUser?.uid) return false;
      try {
         // Query albums collection only
         const albumsQuery = query(
            collection(db, 'albums'),
            where('createdBy', '==', currentUser.uid),
            limit(1),
         );
         const albumsSnapshot = await getDocs(albumsQuery);

         return albumsSnapshot.size > 0;
      } catch (error) {
         console.error('Error checking albums creation status:', error);
         return false;
      }
   };

   // Helper function to check if user can create event
   const canCreateEvent = async (): Promise<boolean> => {
      const hasCreatedEvents = await hasCreatedAnyEvents();

      // Check for paid subscription only (not invited access)
      const hasPaidSubscription =
         hasSubscriptions &&
         currentSubscription &&
         (currentSubscription.status === 'active' ||
            currentSubscription.status === 'trialing' ||
            currentSubscription.status === 'past_due');

      // If user hasn't created any events yet, always allow
      if (!hasCreatedEvents) {
         return true;
      }

      // If user has created events, only allow if they have PAID subscription
      return hasPaidSubscription;
   };

   // Helper function to check if user can create album
   const canCreateAlbum = async (): Promise<boolean> => {
      const hasCreatedAlbums = await hasCreatedAnyAlbums();

      // Check for paid subscription only (not invited access)
      const hasPaidSubscription =
         hasSubscriptions &&
         currentSubscription &&
         (currentSubscription.status === 'active' ||
            currentSubscription.status === 'trialing' ||
            currentSubscription.status === 'past_due');

      // If user hasn't created any albums yet, always allow
      if (!hasCreatedAlbums) {
         return true;
      }

      // If user has created albums, only allow if they have PAID subscription
      return hasPaidSubscription;
   };

   const logAlbumActivity = async (activity: {
      action: string;
      albumId?: string;
      albumTitle?: string;
      details?: string;
      contextId?: string;
   }) => {
      if (!currentUser?.uid) return;
      try {
         await addDoc(collection(db, 'feed_recent_activity'), {
            userId: currentUser.uid,
            action: activity.action,
            albumId: activity.albumId || null,
            albumTitle: activity.albumTitle || null,
            details: activity.details || null,
            contextId: activity.contextId || null,
            actorId: currentUser.uid,
            createdAt: serverTimestamp(),
         });
      } catch (err) {
         console.error('Error logging album activity', err);
      }
   };

   const handleEventCreated = async (eventId: string, eventData?: any) => {
      setEventDialogOpen(false);
      setSnackbarSeverity('success');
      setSnackbarMessage('Event created successfully! View it in your feed');
      setSnackbarOpen(true);

      // Activity tracking is now handled by the backend
      // Refetch activities immediately to show the new event
      setTimeout(() => {
         fetchActivities();
      }, 1000); // Small delay to ensure backend has processed
   };

   const handleCreateAlbum = async (albumData: {
      title: string;
      description?: string;
      type: 'private' | 'public' | 'paid';
      enableParticipantParticipation: boolean;
      pricePerPhoto?: number;
      pricingModel?: 'per_photo' | 'one_time' | 'donation';
      paidAlbumAccessType?: 'public' | 'private';
      albumDate?: string;
   }) => {
      if (!currentUser?.uid) {
         const message = 'User not found';
         setSnackbarSeverity('error');
         setSnackbarMessage(message);
         setSnackbarOpen(true);
         throw new Error(message);
      }

      setAlbumCreating(true);
      try {
         const headers = await getAuthHeaders();
         const payload = {
            title: albumData.title,
            description: albumData.description || '',
            type: albumData.type,
            enableParticipantParticipation: albumData.enableParticipantParticipation,
            createdBy: currentUser.uid,
            albumDate: albumData.albumDate,
            ...(albumData.type === 'paid' && {
               pricePerPhoto: albumData.pricePerPhoto || 5.0,
               pricingModel: albumData.pricingModel || 'per_photo',
               paidAlbumAccessType: albumData.paidAlbumAccessType || 'public',
            }),
         };

         const res = await fetch(`${process.env.REACT_APP_API_URL}/api/albums/create`, {
            method: 'POST',
            headers: {
               ...headers,
               'Content-Type': 'application/json',
            },
            body: JSON.stringify(payload),
         });

         const data = await res.json();
         if (!res.ok) throw new Error(data.message || 'Failed to create album');

         setSnackbarSeverity('success');
         setSnackbarMessage('Album created successfully! View it in your feed');
         setSnackbarOpen(true);
         setAlbumDialogOpen(false);

         // Activity tracking is now handled by the backend
         // Refetch activities immediately to show the new album
         setTimeout(() => {
            fetchActivities();
         }, 1000); // Small delay to ensure backend has processed
      } catch (err: any) {
         const message = err?.message || 'Failed to create album';
         setSnackbarSeverity('error');
         setSnackbarMessage(message);
         setSnackbarOpen(true);
         throw err;
      } finally {
         setAlbumCreating(false);
      }
   };

   // Fetch album details (album doc + photos query in parallel)
   const fetchAlbumDetails = async (albumId: string): Promise<EnrichedAlbumData | null> => {
      try {
         const photosQuery = query(
            collection(db, 'photos'),
            where('albumId', '==', albumId),
            limit(6), // Get up to 6 for stacking
         );

         const [albumDoc, photosSnapshot] = await Promise.all([
            getDoc(doc(db, 'albums', albumId)),
            getDocs(photosQuery),
         ]);

         if (!albumDoc.exists()) {
            return null;
         }

         const albumData = albumDoc.data();

         let coverPhoto = albumData.coverPhotoUrl || albumData.coverPhoto || '';
         const photoUrls: string[] = [];

         const photos = photosSnapshot.docs.filter((d) => !d.data().deleted);
         const totalPhotoCount = photos.length;

         photos.forEach((photoDoc) => {
            const photoData = photoDoc.data();
            const url = photoData.url || photoData.downloadURL;
            if (url) {
               photoUrls.push(url);
               if (!coverPhoto) {
                  coverPhoto = url;
               }
            }
         });

         return {
            coverPhoto,
            photoCount: totalPhotoCount,
            title: albumData.title || 'Untitled Album',
            photos: photoUrls,
         };
      } catch (error) {
         console.error('Error fetching album details:', error);
         return null;
      }
   };

   useEffect(() => {
      let active = true;

      const normalizeAccountTypes = (value: unknown): string[] => {
         if (Array.isArray(value)) {
            return value
               .filter((entry): entry is string => typeof entry === 'string')
               .map((entry) => entry.toLowerCase());
         }
         if (typeof value === 'string' && value.trim()) {
            return [value.toLowerCase()];
         }
         return [];
      };

      /** Same resolution order as server `getUserProfileForAccountType` (albumRoutes): first existing doc wins. */
      const loadEligibility = async () => {
         if (!currentUser?.uid) {
            if (active) {
               setPhotographerEligibility('ineligible');
            }
            return;
         }

         if (active) setPhotographerEligibility('loading');

         try {
            let accountTypes: string[] = [];
            let foundProfile = false;
            for (const collectionName of ['userProfile', 'userProfiles', 'users'] as const) {
               const profileDoc = await getDoc(doc(db, collectionName, currentUser.uid));
               if (profileDoc.exists()) {
                  const profileData = profileDoc.data() as Record<string, unknown>;
                  accountTypes = normalizeAccountTypes(profileData.accountType);
                  foundProfile = true;
                  break;
               }
            }
            if (!foundProfile) {
               accountTypes = normalizeAccountTypes(currentUser.accountType);
            }

            const isIneligible =
               accountTypes.includes('photographer') || accountTypes.includes('organization');
            if (active) {
               setPhotographerEligibility(isIneligible ? 'ineligible' : 'eligible');
            }
         } catch (error) {
            console.warn('Unable to resolve account type for photographer upsell:', error);
            const fallback = normalizeAccountTypes(currentUser.accountType);
            const isIneligible =
               fallback.includes('photographer') || fallback.includes('organization');
            if (active) {
               setPhotographerEligibility(isIneligible ? 'ineligible' : 'eligible');
            }
         }
      };

      loadEligibility();
      return () => {
         active = false;
      };
   }, [currentUser?.uid, currentUser?.accountType, photographerEligibilityRefreshKey]);

   // Build photographer feed data from accessible albums and photos
   useEffect(() => {
      const loadPhotographers = async () => {
         if (!currentUser) {
            setPhotographerCards([]);
            return;
         }

         if (!accessibleAlbums || accessibleAlbums.length === 0) {
            setPhotographerCards([]);
            return;
         }

         try {
            type PhotoAgg = {
               url: string;
               uploadedAt: number;
               albumId: string;
            };

            const byPhotographer: Record<
               string,
               { userId: string; photos: PhotoAgg[]; albumIds: Set<string> }
            > = {};

            // Batch-query photos across accessible albums (max 30 IDs per 'in' clause)
            const albumIds = accessibleAlbums.map((a) => a.id);
            const chunks: string[][] = [];
            for (let i = 0; i < albumIds.length; i += 30) {
               chunks.push(albumIds.slice(i, i + 30));
            }

            const snapshots = await Promise.all(
               chunks.map((chunk) =>
                  getDocs(
                     query(collection(db, 'photos'), where('albumId', 'in', chunk), limit(60)),
                  ),
               ),
            );

            for (const snapshot of snapshots) {
               snapshot.forEach((docSnap) => {
                  const data = docSnap.data() as any;
                  if (data.deleted) return;

                  const uploaderId: string | undefined = data.uploadedBy || data.userId;
                  if (!uploaderId) return;

                  const url: string | undefined = data.thumbnailUrl || data.url || data.downloadURL;
                  if (!url) return;

                  let uploadedAtMs = Date.now();
                  const uploadedAt = data.uploadedAt || data.createdAt;
                  if (uploadedAt instanceof Timestamp) {
                     uploadedAtMs = uploadedAt.toDate().getTime();
                  } else if (uploadedAt && typeof uploadedAt.toDate === 'function') {
                     uploadedAtMs = uploadedAt.toDate().getTime();
                  }

                  if (!byPhotographer[uploaderId]) {
                     byPhotographer[uploaderId] = {
                        userId: uploaderId,
                        photos: [],
                        albumIds: new Set<string>(),
                     };
                  }

                  byPhotographer[uploaderId].photos.push({
                     url,
                     uploadedAt: uploadedAtMs,
                     albumId: data.albumId,
                  });
                  byPhotographer[uploaderId].albumIds.add(data.albumId);
               });
            }

            const photographerIds = Object.keys(byPhotographer);
            if (photographerIds.length === 0) {
               setPhotographerCards([]);
               return;
            }

            const limitedIds = photographerIds.slice(0, 20);
            const headers = await getAuthHeaders();

            const results = await Promise.all(
               limitedIds.map(async (userId) => {
                  try {
                     const res = await fetch(
                        `${process.env.REACT_APP_API_URL}/api/profile/public/${userId}`,
                        { headers },
                     );
                     if (!res.ok) return null;
                     const data = await res.json();

                     const photographerProfile = data?.photographerProfile || null;
                     if (!photographerProfile) return null;

                     const agg = byPhotographer[userId];
                     const sortedPhotos = [...agg.photos].sort(
                        (a, b) => b.uploadedAt - a.uploadedAt,
                     );
                     const recentPhotoUrls = sortedPhotos.map((p) => p.url).slice(0, 6);
                     const lastActivityAtMs = sortedPhotos[0]?.uploadedAt ?? null;

                     const displayName: string =
                        photographerProfile.name ||
                        data.displayName ||
                        data.name ||
                        data.email?.split?.('@')?.[0] ||
                        'Photographer';

                     const profilePhotoUrl: string | undefined =
                        photographerProfile.profilePhotoUrl ||
                        data.photoURL ||
                        data.profilePhoto ||
                        data.avatar ||
                        undefined;

                     const card: PhotographerFeedCardData = {
                        userId,
                        displayName,
                        profilePhotoUrl,
                        photographerProfile,
                        photoCount: agg.photos.length,
                        albumCount: agg.albumIds.size,
                        recentPhotoUrls,
                        lastActivityAt: lastActivityAtMs ? new Date(lastActivityAtMs) : null,
                     };

                     return card;
                  } catch (error) {
                     console.error('Error loading photographer profile', error);
                     return null;
                  }
               }),
            );

            const filtered = results.filter((c): c is PhotographerFeedCardData => !!c);
            setPhotographerCards(filtered);
         } catch (error) {
            console.error('Error building photographer feed cards', error);
            setPhotographerCards([]);
         }
      };

      loadPhotographers();
   }, [currentUser, accessibleAlbums]);

   // Fetch user details (optimized - minimal logging)
   const fetchUserDetails = useCallback(
      async (userId: string): Promise<EnrichedUserData | null> => {
         try {
            const userDoc = await getDoc(doc(db, 'users', userId));
            if (userDoc.exists()) {
               const userData = userDoc.data();
               return {
                  displayName:
                     userData.displayName || userData.email?.split('@')[0] || 'Unknown User',
                  photoURL: userData.photoURL || '',
                  email: userData.email || '',
               };
            }
            return null;
         } catch (error) {
            console.error('Error fetching user details:', error);
            return null;
         }
      },
      [],
   );

   useEffect(() => {
      if (!currentUser) return;

      const q = query(
         collection(db, 'feed'),
         where('card_type', '==', 'album_access_request'),
         where('userid', '==', currentUser.uid),
         where('seen', '==', false),
         limit(20),
      );

      const unsubscribe = onSnapshot(
         q,
         async (snapshot) => {
            const requests: AlbumAccessRequest[] = [];
            const albumIds = new Set<string>();
            const userIds = new Set<string>();

            snapshot.forEach((doc) => {
               const data = doc.data();

               // Collect IDs for batch fetching (only new ones not in cache)
               if (data.albumId && !albumDetails[data.albumId]) {
                  albumIds.add(data.albumId);
               }

               if (data.senderid && !userDetails[data.senderid]) {
                  userIds.add(data.senderid);
               }

               // Map feed card data to AlbumAccessRequest format
               requests.push({
                  id: doc.id,
                  requesterId: data.senderid || '',
                  requesterName: data.sendername || 'Unknown User',
                  requesterPhoto: data.senderphoto,
                  requesterRole: data.senderrole,
                  albumId: data.albumId || '',
                  albumName: data.albumName || 'Untitled Album',
                  albumPhotoCount: data.albumPhotoCount || 0,
                  albumOwnerId: data.userid || '',
                  accessMethod: (data.accessMethod || 'link') as 'qr' | 'link' | 'email',
                  status: data.status || 'pending',
                  timestamp: data.createdAt || Timestamp.now(),
               } as AlbumAccessRequest);
            });

            // Sort by timestamp (most recent first)
            requests.sort((a, b) => {
               const aTime = a.timestamp?.seconds || 0;
               const bTime = b.timestamp?.seconds || 0;
               return bTime - aTime;
            });

            // Show cards immediately with existing cached data
            setAccessRequests(requests);
            setLoading(false);

            // Fetch enriched data in background for new items only
            if (albumIds.size > 0 || userIds.size > 0) {
               const newAlbumDetails: Record<string, EnrichedAlbumData> = { ...albumDetails };
               const newUserDetails: Record<string, EnrichedUserData> = { ...userDetails };

               // Fetch in parallel for better performance
               const [albumResults, userResults] = await Promise.all([
                  // Fetch all album details
                  Promise.all(
                     Array.from(albumIds).map(async (albumId) => ({
                        albumId,
                        details: await fetchAlbumDetails(albumId),
                     })),
                  ),
                  // Fetch all user details
                  Promise.all(
                     Array.from(userIds).map(async (userId) => ({
                        userId,
                        details: await fetchUserDetails(userId),
                     })),
                  ),
               ]);

               // Update album details
               albumResults.forEach(({ albumId, details }) => {
                  if (details) {
                     newAlbumDetails[albumId] = details;
                  }
               });

               // Update user details
               userResults.forEach(({ userId, details }) => {
                  if (details) {
                     newUserDetails[userId] = details;
                  }
               });

               // Update state with enriched data
               setAlbumDetails(newAlbumDetails);
               setUserDetails(newUserDetails);
            }
         },
         (error) => {
            console.error('Error fetching access requests:', error);
            setAccessRequests([]);
            setLoading(false);
         },
      );

      return () => unsubscribe();
   }, [currentUser]);

   // Fetch album invitations SENT by the current user (for sender's view)
   useEffect(() => {
      if (!currentUser) {
         setSentInvitations([]);
         setLoadingSentInvitations(false);
         return;
      }

      setLoadingSentInvitations(true);

      const sentQuery = query(
         collection(db, 'feed'),
         where('card_type', '==', 'album_invitation_sent'),
         where('userid', '==', currentUser.uid),
         where('seen', '==', false),
         limit(50),
      );

      const unsubscribeSentInvites = onSnapshot(
         sentQuery,
         async (snapshot) => {
            const invitations: AlbumInvitationSent[] = [];

            snapshot.forEach((docSnapshot) => {
               const data = docSnapshot.data();

               // Handle recipients array (new format) or single recipient (old format)
               const recipients = data.recipients || [
                  {
                     recipientEmail: data.recipientEmail || '',
                     recipientName: data.recipientName || '',
                     recipientPhoto: data.recipientPhoto || '',
                     status: data.status || 'pending',
                     role: data.role || 'viewer',
                  },
               ];

               invitations.push({
                  id: docSnapshot.id,
                  batchId: data.batchId || docSnapshot.id,
                  albumId: data.albumId || '',
                  albumTitle: data.albumTitle || 'Untitled Album',
                  albumCoverUrl: data.albumCoverUrl || '',
                  albumPhotoCount: data.albumPhotoCount || 0,
                  albumIsPublic: data.albumIsPublic || false,
                  recipients: recipients,
                  sentAt: data.sentAt || data.createdAt,
                  expiresAt: data.expiresAt,
                  lastResentAt: data.lastResentAt,
                  isReminder: data.isReminder || false,
               });
            });

            console.log('📊 Sent invitations found (before merge):', invitations.length);

            // MERGE all recipients from cards for the same album into ONE card
            // Prioritize the most up-to-date status for each recipient
            const albumMap = new Map<string, AlbumInvitationSent>();
            invitations.forEach((inv) => {
               const existing = albumMap.get(inv.albumId);
               if (!existing) {
                  albumMap.set(inv.albumId, { ...inv });
               } else {
                  // Merge recipients - update status for existing recipients, add new ones
                  const recipientMap = new Map<string, any>();

                  // Add existing recipients first
                  (existing.recipients || []).forEach((r) => {
                     const email = r.recipientEmail?.toLowerCase() || '';
                     if (email) {
                        recipientMap.set(email, r);
                     }
                  });

                  // Update or add recipients from the new invitation
                  // This ensures the latest status is always used
                  (inv.recipients || []).forEach((r) => {
                     const email = r.recipientEmail?.toLowerCase() || '';
                     if (email) {
                        const existingRecipient = recipientMap.get(email);
                        if (!existingRecipient) {
                           // New recipient
                           recipientMap.set(email, r);
                        } else {
                           // Update existing recipient with latest status
                           recipientMap.set(email, {
                              ...existingRecipient,
                              ...r,
                              // Always use the most specific/latest status
                              status: r.status !== 'pending' ? r.status : existingRecipient.status,
                           });
                        }
                     }
                  });

                  existing.recipients = Array.from(recipientMap.values());

                  // Keep the most recent timestamp
                  const existingTime = existing.sentAt?.seconds || 0;
                  const newTime = inv.sentAt?.seconds || 0;
                  if (newTime > existingTime) {
                     existing.sentAt = inv.sentAt;
                     existing.lastResentAt = inv.lastResentAt;
                     existing.isReminder = inv.isReminder;
                  }
               }
            });

            const mergedInvitations = Array.from(albumMap.values());
            console.log('📊 Sent invitations after merge:', mergedInvitations.length);
            console.log(
               '📊 Recipients per album:',
               mergedInvitations.map((inv) => ({
                  album: inv.albumTitle,
                  recipients: inv.recipients?.length,
               })),
            );

            // Sort by sent date (most recent first)
            mergedInvitations.sort((a, b) => {
               const aTime = a.sentAt?.seconds || 0;
               const bTime = b.sentAt?.seconds || 0;
               return bTime - aTime;
            });

            setSentInvitations(mergedInvitations);
            setLoadingSentInvitations(false);
         },
         (error) => {
            console.error('❌ Error fetching sent invitations:', error);
            setSentInvitations([]);
            setLoadingSentInvitations(false);
         },
      );

      return () => unsubscribeSentInvites();
   }, [currentUser]);

   // Fetch album invitations RECEIVED by the current user (for recipient's view)
   useEffect(() => {
      if (!currentUser) {
         setReceivedInvitations([]);
         setLoadingReceivedInvitations(false);
         return;
      }

      setLoadingReceivedInvitations(true);

      const receivedQuery = query(
         collection(db, 'feed'),
         where('card_type', '==', 'album_invitation_received'),
         where('recipientId', '==', currentUser.uid),
         where('seen', '==', false),
         limit(20),
      );

      const unsubscribeReceivedInvites = onSnapshot(
         receivedQuery,
         async (snapshot) => {
            const invitations: AlbumInvitationReceived[] = [];

            snapshot.forEach((docSnapshot) => {
               const data = docSnapshot.data();

               if (data.status === 'removed') return;

               invitations.push({
                  id: docSnapshot.id,
                  invitationId: data.invitationId || '',
                  albumId: data.albumId || '',
                  albumTitle: data.albumTitle || 'Untitled Album',
                  albumCoverUrl: data.albumCoverUrl || '',
                  albumPhotoCount: data.albumPhotoCount || 0,
                  albumIsPublic: data.albumIsPublic || false,
                  senderName: data.senderName || 'Someone',
                  senderEmail: data.senderEmail || '',
                  senderPhotoUrl: data.senderPhoto || '',
                  role: data.role || 'viewer',
                  status: data.status || 'pending',
                  sentAt: data.sentAt || data.createdAt,
                  expiresAt: data.expiresAt,
               });
            });

            // Sort by sent date (most recent first)
            invitations.sort((a, b) => {
               const aTime = a.sentAt?.seconds || 0;
               const bTime = b.sentAt?.seconds || 0;
               return bTime - aTime;
            });

            setReceivedInvitations(invitations);
            setLoadingReceivedInvitations(false);
         },
         (error) => {
            console.error('❌ Error fetching received invitations:', error);
            setReceivedInvitations([]);
            setLoadingReceivedInvitations(false);
         },
      );

      return () => unsubscribeReceivedInvites();
   }, [currentUser]);

   // Fetch album link clicks for album owners (views via shared link or QR code)
   useEffect(() => {
      if (!currentUser) {
         setAlbumLinkClicks([]);
         setLoadingLinkClicks(false);
         return;
      }

      console.log('🔍 Fetching album_link_click cards for user:', currentUser.uid);
      setLoadingLinkClicks(true);

      const feedRef = collection(db, 'feed');

      // Query for album_link_click cards where current user is the album owner
      const linkClickQuery = query(
         feedRef,
         where('card_type', '==', 'album_link_click'),
         where('userid', '==', currentUser.uid),
         where('seen', '==', false),
         limit(20),
      );

      const unsubscribeLinkClicks = onSnapshot(
         linkClickQuery,
         (snapshot) => {
            const clicks: AlbumLinkClick[] = [];

            snapshot.forEach((docSnapshot) => {
               const data = docSnapshot.data();

               console.log('📋 Feed - Raw feed card data:', {
                  id: docSnapshot.id,
                  viewers: data.viewers,
                  viewersCount: data.viewers?.length || 0,
                  viewer: data.viewer,
               });

               clicks.push({
                  id: docSnapshot.id,
                  albumId: data.albumId || '',
                  albumTitle: data.albumTitle || 'Untitled Album',
                  albumCoverUrl: data.albumCoverUrl || '',
                  albumPhotoCount: data.albumPhotoCount || 0,
                  albumIsPublic: data.albumIsPublic || false,
                  albumPhotos: data.albumPhotos || [],
                  viewers: data.viewers || [], // Array of viewers
                  viewer: data.viewer || {
                     viewerId: null,
                     viewerName: 'Anonymous',
                     viewerEmail: null,
                     viewerPhotoURL: null,
                     viewerJobTitle: null,
                  },
                  source: data.source || 'shareable_link',
                  totalLinkClicks: data.totalLinkClicks || 0,
                  totalQRScans: data.totalQRScans || 0,
                  percentageChange: data.percentageChange || 0,
                  clickedAt: data.clickedAt || data.createdAt,
                  isFirstView: data.isFirstView || false,
               });
            });

            console.log('📊 Link click cards found:', clicks.length);
            console.log(
               '📊 Link click cards viewers:',
               clicks.map((c) => ({ albumId: c.albumId, viewersCount: c.viewers?.length || 0 })),
            );

            // Sort by clicked date (most recent first)
            clicks.sort((a, b) => {
               const aTime = a.clickedAt?.seconds || 0;
               const bTime = b.clickedAt?.seconds || 0;
               return bTime - aTime;
            });

            setAlbumLinkClicks(clicks);
            setLoadingLinkClicks(false);
         },
         (error) => {
            console.error('❌ Error fetching link click cards:', error);
            setAlbumLinkClicks([]);
            setLoadingLinkClicks(false);
         },
      );

      return () => unsubscribeLinkClicks();
   }, [currentUser]);

   // Check profile completion status and fetch profile data
   useEffect(() => {
      const fetchProfileStatus = async () => {
         if (!currentUser) {
            setLoadingProfileStatus(false);
            return;
         }

         try {
            // Fetch profile completion status
            const status = await checkProfileCompletion(
               currentUser.uid,
               currentUser.displayName,
               currentUser.photoURL,
            );
            setProfileStatus(status);

            // Fetch full profile data for the edit dialog
            const profileDoc = await getDoc(doc(db, 'userProfiles', currentUser.uid));
            if (profileDoc.exists()) {
               const data = profileDoc.data();
               setProfileData({
                  displayName: currentUser.displayName || '',
                  email: currentUser.email || '',
                  bio: data.bio || '',
                  location: data.location || '',
                  title: data.title || '',
                  companyName: data.companyName || '',
                  website: data.website || '',
                  phoneNumber: data.phoneNumber || '',
                  facebook: data.facebook || '',
                  twitter: data.twitter || '',
                  linkedin: data.linkedin || '',
                  instagram: data.instagram || '',
                  tagline: data.tagline || '',
                  languages: data.languages || '',
                  whatIDo: data.whatIDo || [],
                  interests: data.interests || [],
                  lookingFor: data.lookingFor || [],
                  featuredProjects: data.featuredProjects || [],
                  photoURL: currentUser.photoURL || '',
               });
            } else {
               // Set default profile data
               setProfileData({
                  displayName: currentUser.displayName || '',
                  email: currentUser.email || '',
                  photoURL: currentUser.photoURL || '',
                  bio: '',
                  location: '',
                  languages: '',
               });
            }
         } catch (error) {
            console.error('Error checking profile completion:', error);
         } finally {
            setLoadingProfileStatus(false);
         }
      };

      fetchProfileStatus();
   }, [currentUser]);

   // Fetch album downloads for album owners
   useEffect(() => {
      if (!currentUser) {
         setAlbumDownloads([]);
         setLoadingDownloads(false);
         return;
      }

      console.log('🔍 Fetching album download cards for user:', currentUser.uid);
      console.log('🔍 Collection: albumDownloadFeed');
      console.log('🔍 Query: ownerId ==', currentUser.uid);
      setLoadingDownloads(true);

      const downloadFeedRef = collection(db, 'albumDownloadFeed');

      // Query for download cards where current user is the album owner
      // Note: This requires a composite index in Firestore
      let downloadQuery;

      try {
         // Try with orderBy first (requires index)
         downloadQuery = query(
            downloadFeedRef,
            where('ownerId', '==', currentUser.uid),
            orderBy('lastDownloadAt', 'desc'),
            limit(20),
         );
      } catch (error) {
         // Fallback to simple query without orderBy
         console.log(
            '⚠️ Using simple query (no orderBy) - create Firestore index for better sorting',
         );
         downloadQuery = query(downloadFeedRef, where('ownerId', '==', currentUser.uid), limit(20));
      }

      console.log('🔍 Download query created, setting up snapshot listener...');

      const unsubscribeDownloads = onSnapshot(
         downloadQuery,
         (snapshot) => {
            const downloads: AlbumDownload[] = [];

            snapshot.forEach((docSnapshot) => {
               const data = docSnapshot.data();

               console.log('📥 Feed - Raw download card data:', {
                  id: docSnapshot.id,
                  downloaders: data.downloaders,
                  downloadersCount: data.downloaders?.length || 0,
                  downloader: data.downloader,
               });

               downloads.push({
                  id: docSnapshot.id,
                  albumId: data.albumId || '',
                  albumTitle: data.albumTitle || 'Untitled Album',
                  albumCoverUrl: data.albumCoverUrl || '',
                  albumPhotoCount: data.albumPhotoCount || 0,
                  albumIsPublic: data.albumIsPublic || false,
                  albumPhotos: data.albumPhotos || [],
                  downloaders: data.downloaders || [], // Array of downloaders
                  downloader: data.downloader || {
                     downloaderId: '',
                     downloaderName: 'Unknown User',
                     downloaderEmail: null,
                     downloaderPhotoURL: null,
                  },
                  totalDownloads: data.totalDownloads || 0,
                  percentageChange: data.percentageChange || 0,
                  lastDownloadAt: data.lastDownloadAt || data.createdAt,
               });
            });

            console.log('📥 Download cards found:', downloads.length);
            console.log(
               '📥 Download cards downloaders:',
               downloads.map((d) => ({
                  albumId: d.albumId,
                  downloadersCount: d.downloaders?.length || 0,
               })),
            );

            // Sort by download date (most recent first)
            downloads.sort((a, b) => {
               const aTime = a.lastDownloadAt?.seconds || 0;
               const bTime = b.lastDownloadAt?.seconds || 0;
               return bTime - aTime;
            });

            setAlbumDownloads(downloads);
            setLoadingDownloads(false);
         },
         (error) => {
            console.error('❌ Error fetching download cards:', error);
            console.error('❌ Error details:', error.message);

            // If it's an index error, try again with simple query
            if (error.message && error.message.includes('index')) {
               console.log('⚠️ Firestore index required. Retrying with simple query...');
               const simpleQuery = query(
                  downloadFeedRef,
                  where('ownerId', '==', currentUser.uid),
                  limit(20),
               );

               onSnapshot(
                  simpleQuery,
                  (snapshot) => {
                     const downloads: AlbumDownload[] = [];
                     snapshot.forEach((docSnapshot) => {
                        const data = docSnapshot.data();
                        downloads.push({
                           id: docSnapshot.id,
                           albumId: data.albumId || '',
                           albumTitle: data.albumTitle || 'Untitled Album',
                           albumCoverUrl: data.albumCoverUrl || '',
                           albumPhotoCount: data.albumPhotoCount || 0,
                           albumIsPublic: data.albumIsPublic || false,
                           albumPhotos: data.albumPhotos || [],
                           downloaders: data.downloaders || [],
                           downloader: data.downloader || {
                              downloaderId: '',
                              downloaderName: 'Unknown User',
                              downloaderEmail: null,
                              downloaderPhotoURL: null,
                           },
                           totalDownloads: data.totalDownloads || 0,
                           percentageChange: data.percentageChange || 0,
                           lastDownloadAt: data.lastDownloadAt || data.createdAt,
                        });
                     });

                     // Manual sort by lastDownloadAt
                     downloads.sort((a, b) => {
                        const aTime = a.lastDownloadAt?.seconds || 0;
                        const bTime = b.lastDownloadAt?.seconds || 0;
                        return bTime - aTime;
                     });

                     setAlbumDownloads(downloads);
                     setLoadingDownloads(false);
                     console.log('✅ Loaded', downloads.length, 'download cards with simple query');
                  },
                  (retryError) => {
                     console.error('❌ Retry failed:', retryError);
                     setAlbumDownloads([]);
                     setLoadingDownloads(false);
                  },
               );
            } else {
               setAlbumDownloads([]);
               setLoadingDownloads(false);
            }
         },
      );

      return () => unsubscribeDownloads();
   }, [currentUser]);

   // Fetch albums created by user
   useEffect(() => {
      if (!currentUser) {
         setAlbumsCreated([]);
         return;
      }

      console.log('🔍 Fetching album_created cards for user:', currentUser.uid);

      const feedRef = collection(db, 'feed');
      const albumCreatedQuery = query(
         feedRef,
         where('card_type', '==', 'album_created'),
         where('userid', '==', currentUser.uid),
         limit(100),
      );

      const unsubscribeAlbumsCreated = onSnapshot(
         albumCreatedQuery,
         async (snapshot) => {
            const albums: AlbumCreated[] = [];

            snapshot.forEach((docSnapshot) => {
               const data = docSnapshot.data();
               albums.push({
                  id: docSnapshot.id,
                  card_type: 'album_created',
                  userid: data.userid || '',
                  albumId: data.albumId || '',
                  albumTitle: data.albumTitle || 'Untitled Album',
                  albumDescription: data.albumDescription || '',
                  albumCoverUrl: data.albumCoverUrl || '',
                  albumPhotoCount: data.albumPhotoCount || 0,
                  albumType: data.albumType || 'private',
                  albumIsPublic: data.albumIsPublic || false,
                  albumDate: data.albumDate || '',
                  albumPhotos: data.albumPhotos || [],
                  creatorId: data.creatorId || '',
                  creatorName: data.creatorName || 'Unknown',
                  creatorPhoto: data.creatorPhoto || '',
                  creatorEmail: data.creatorEmail || '',
                  createdAt: data.createdAt,
                  seen: data.seen || false,
                  fromInvitationAccept: !!data.fromInvitationAccept,
                  invitationMemberRole: data.invitationMemberRole || undefined,
                  accessGrantedVia: data.accessGrantedVia || undefined,
               });
            });

            console.log('📊 Album created cards found:', albums.length);

            // Refresh album doc for count/cover; photo strip uses InlinePhotoScroller API (no per-photo Firestore reads)
            await Promise.all(
               albums.map(async (album) => {
                  try {
                     const albumDoc = await getDoc(doc(db, 'albums', album.albumId));
                     if (!albumDoc.exists()) return;
                     const albumData = albumDoc.data();
                     const currentIds: string[] = albumData.photoIds || [];
                     album.albumPhotoCount = currentIds.length;
                     if (!album.albumCoverUrl && albumData.coverPhotoUrl) {
                        album.albumCoverUrl = albumData.coverPhotoUrl;
                     }
                     album.albumPhotos = [];
                  } catch {
                     // Keep whatever data was in the feed doc
                  }
               }),
            );

            // Sort by creation date (most recent first)
            albums.sort((a, b) => {
               const aTime = a.createdAt?.seconds || 0;
               const bTime = b.createdAt?.seconds || 0;
               return bTime - aTime;
            });

            setAlbumsCreated(albums);
         },
         (error) => {
            console.error('❌ Error fetching album created cards:', error);
            setAlbumsCreated([]);
         },
      );

      return () => unsubscribeAlbumsCreated();
   }, [currentUser]);

   // Fetch events created by user
   useEffect(() => {
      if (!currentUser) {
         setEventsCreated([]);
         return;
      }

      console.log('🔍 Fetching event_created cards for user:', currentUser.uid);

      const feedRef = collection(db, 'feed');
      const eventCreatedQuery = query(
         feedRef,
         where('card_type', '==', 'event_created'),
         where('userid', '==', currentUser.uid),
         limit(20),
      );

      const unsubscribeEventsCreated = onSnapshot(
         eventCreatedQuery,
         (snapshot) => {
            const events: EventCreated[] = [];

            snapshot.forEach((docSnapshot) => {
               const data = docSnapshot.data();

               // Handle location - convert object to string if needed
               let locationString = '';
               if (data.eventLocation) {
                  if (typeof data.eventLocation === 'string') {
                     locationString = data.eventLocation;
                  } else if (typeof data.eventLocation === 'object') {
                     locationString = data.eventLocation.name || data.eventLocation.address || '';
                  }
               }

               events.push({
                  id: docSnapshot.id,
                  card_type: 'event_created',
                  userid: data.userid || '',
                  eventId: data.eventId || '',
                  eventTitle: data.eventTitle || 'Untitled Event',
                  eventDescription: data.eventDescription || '',
                  eventCoverUrl: data.eventCoverUrl || '',
                  eventStartDate: data.eventStartDate || '',
                  eventEndDate: data.eventEndDate || '',
                  eventLocation: locationString,
                  eventType: data.eventType || '',
                  eventIsPublic: data.eventIsPublic || false,
                  eventIsVirtual: data.eventIsVirtual || false,
                  creatorId: data.creatorId || '',
                  creatorName: data.creatorName || 'Unknown',
                  creatorPhoto: data.creatorPhoto || '',
                  creatorEmail: data.creatorEmail || '',
                  createdAt: data.createdAt,
                  seen: data.seen || false,
               });
            });

            console.log('📊 Event created cards found:', events.length);

            // Sort by creation date (most recent first)
            events.sort((a, b) => {
               const aTime = a.createdAt?.seconds || 0;
               const bTime = b.createdAt?.seconds || 0;
               return bTime - aTime;
            });

            setEventsCreated(events);
         },
         (error) => {
            console.error('❌ Error fetching event created cards:', error);
            setEventsCreated([]);
         },
      );

      return () => unsubscribeEventsCreated();
   }, [currentUser]);

   // Enrich a batch of album IDs into full AccessibleAlbum objects
   const enrichAlbumBatch = useCallback(
      async (albumIds: string[]): Promise<AccessibleAlbum[]> => {
         if (!currentUser || albumIds.length === 0) return [];

         const headers = await getAuthHeaders();
         const albumPromises = albumIds.map(async (albumId) => {
            try {
               const albumResponse = await fetch(
                  `${process.env.REACT_APP_API_URL}/api/albums/${albumId}?currentUserId=${currentUser.uid}`,
                  { headers },
               );
               if (!albumResponse.ok) return null;
               const albumResult = await albumResponse.json();
               return albumResult.album || null;
            } catch {
               return null;
            }
         });

         const albums = (await Promise.all(albumPromises)).filter(Boolean);

         const userEmail = currentUser.email?.toLowerCase() || '';
         const memberAlbums = albums.filter((album: any) => {
            if (album.createdBy === currentUser.uid || album.owner === currentUser.uid) return true;
            const members = album.members || [];
            return members.some(
               (member: any) =>
                  member.userId === currentUser.uid || member.email?.toLowerCase() === userEmail,
            );
         });

         const uniqueOwnerIds = Array.from(
            new Set(
               memberAlbums
                  .map((a: any) => a.createdBy || a.owner)
                  .filter((id: unknown): id is string => typeof id === 'string' && id.length > 0),
            ),
         );
         const ownerResults = await Promise.all(uniqueOwnerIds.map((id) => fetchUserDetails(id)));
         const ownerById = new Map<string, EnrichedUserData | null>(
            uniqueOwnerIds.map((id, i) => [id, ownerResults[i]]),
         );

         const enrichedAlbums = await Promise.all(
            memberAlbums.map(async (album: any) => {
               const ownerId = album.createdBy || album.owner;
               const ownerDetails =
                  typeof ownerId === 'string' && ownerId.length > 0
                     ? ownerById.get(ownerId) ?? null
                     : null;

               const [albumPhotoDetails, eventDocSnap] = await Promise.all([
                  fetchAlbumDetails(album.id),
                  album.eventId
                     ? getDoc(doc(db, 'events', album.eventId)).catch(() => null)
                     : Promise.resolve(null),
               ]);

               let eventDetails: { title?: string; date?: any } | null = null;
               if (eventDocSnap && eventDocSnap.exists()) {
                  try {
                     const eventData = eventDocSnap.data();
                     eventDetails = { title: eventData.title, date: eventData.startDate };
                  } catch {
                     eventDetails = null;
                  }
               }

               const uid = currentUser.uid;
               let feedMemberRole: AccessibleAlbum['feedMemberRole'] = null;
               if (album.createdBy === uid || album.owner === uid) {
                  feedMemberRole = 'owner';
               } else if (album.members && Array.isArray(album.members)) {
                  const found = album.members.find((m: any) => m.userId === uid);
                  if (found) {
                     const r = String(found.role || '')
                        .toLowerCase()
                        .trim();
                     feedMemberRole =
                        r === 'owner' || r === 'editor' ? (r as 'owner' | 'editor') : 'viewer';
                  }
               }
               if (!feedMemberRole && album.memberIds?.includes(uid)) {
                  feedMemberRole = 'viewer';
               }

               return {
                  id: album.id,
                  title: album.title,
                  description: album.description,
                  type: album.type || 'private',
                  coverPhotoUrl: album.coverPhotoUrl || albumPhotoDetails?.coverPhoto,
                  photoCount: album.photoIds?.length || albumPhotoDetails?.photoCount || 0,
                  owner: album.createdBy || album.owner,
                  ownerName: ownerDetails?.displayName,
                  ownerPhoto: ownerDetails?.photoURL,
                  createdAt: album.createdAt,
                  accessGrantedAt: album.accessGrantedAt,
                  eventId: album.eventId,
                  eventTitle: eventDetails?.title,
                  eventDate: eventDetails?.date,
                  photos: albumPhotoDetails?.photos || [],
                  groupCount: album.groupCount || 0,
                  feedMemberRole,
                  enableParticipantUpload: album.enableParticipantUpload === true,
                  allowPhotographerUpload: album.allowPhotographerUpload === true,
               } as AccessibleAlbum;
            }),
         );

         return enrichedAlbums;
      },
      [currentUser, fetchUserDetails],
   );

   // Merge `album_created` feed docs with albums from membership API (covers legacy users without feed cards)
   const mergedAlbumFeedCards = useMemo(() => {
      if (!currentUser) {
         return [...albumsCreated].sort(
            (a, b) => feedCardTimestampMillis(b.createdAt) - feedCardTimestampMillis(a.createdAt),
         );
      }

      const feedByAlbumId = new Map<string, AlbumCreated>();
      for (const c of albumsCreated) {
         if (c.albumId) feedByAlbumId.set(c.albumId, c);
      }

      const merged: AlbumCreated[] = [...albumsCreated];
      const seen = new Set(feedByAlbumId.keys());

      if (membershipAlbumsForFeed?.albums?.length) {
         const { albums, creators } = membershipAlbumsForFeed;
         for (const album of albums) {
            if (!album?.id || seen.has(album.id)) continue;
            const createdBy = album.createdBy || album.owner;
            merged.push(
               albumDocToSyntheticFeedCard(
                  album,
                  currentUser.uid,
                  createdBy ? (creators[createdBy] ?? null) : null,
               ),
            );
         }
      }

      merged.sort(
         (a, b) => feedCardTimestampMillis(b.createdAt) - feedCardTimestampMillis(a.createdAt),
      );
      return merged;
   }, [albumsCreated, membershipAlbumsForFeed, currentUser]);

   // Fetch albums user has access to (only those approved through feed)
   useEffect(() => {
      const fetchAccessibleAlbums = async () => {
         if (!currentUser) {
            setMembershipAlbumsForFeed(null);
            return;
         }

         setLoadingAlbums(true);
         try {
            const headers = await getAuthHeaders();
            const userAlbumsRes = await fetch(
               `${process.env.REACT_APP_API_URL}/api/albums/user/${currentUser.uid}`,
               { headers },
            );

            if (userAlbumsRes.ok) {
               const data = await userAlbumsRes.json();
               const albums = Array.isArray(data.albums) ? data.albums : [];

               const creatorIds = Array.from(
                  new Set<string>(
                     albums
                        .map((a: any) => a?.createdBy || a?.owner)
                        .filter(
                           (id: unknown): id is string => typeof id === 'string' && id.length > 0,
                        ),
                  ),
               );
               const creators: Record<string, EnrichedUserData | null> = {};
               await Promise.all(
                  creatorIds.map(async (cid) => {
                     creators[cid] = await fetchUserDetails(cid);
                  }),
               );

               setMembershipAlbumsForFeed({ albums, creators });

               if (albums.length === 0) {
                  setAccessibleAlbums([]);
                  setAllAlbumIds([]);
                  setHasMoreAlbums(false);
                  setLoadingAlbums(false);
                  return;
               }

               const ids = albums.map((a: any) => a.id).filter(Boolean);
               setAllAlbumIds(ids);

               const firstBatch = ids.slice(0, ALBUM_PAGE_SIZE);
               const enriched = await enrichAlbumBatch(firstBatch);

               setAccessibleAlbums(enriched);
               setHasMoreAlbums(ids.length > ALBUM_PAGE_SIZE);
               return;
            }

            console.warn(
               'Feed: /api/albums/user failed, using fallback album sources',
               userAlbumsRes.status,
            );
            setMembershipAlbumsForFeed(null);

            const feedRef = collection(db, 'feed');
            const approvedRequestsQuery = query(
               feedRef,
               where('card_type', '==', 'album_access_request'),
               where('senderid', '==', currentUser.uid),
               where('status', '==', 'approved'),
            );

            const approvedRequestsSnapshot = await getDocs(approvedRequestsQuery);

            const approvedAlbumIds = new Set<string>();
            approvedRequestsSnapshot.forEach((docSnap) => {
               const data = docSnap.data();
               if (data.status === 'removed') return;
               if (data.albumId) approvedAlbumIds.add(data.albumId);
            });

            const albumsRef = collection(db, 'albums');
            const ownedAlbumsQuery = query(
               albumsRef,
               where('createdBy', '==', currentUser.uid),
               limit(100),
            );
            const ownedSnapshot = await getDocs(ownedAlbumsQuery);
            ownedSnapshot.docs.forEach((d) => approvedAlbumIds.add(d.id));

            if (approvedAlbumIds.size === 0) {
               setAccessibleAlbums([]);
               setAllAlbumIds([]);
               setHasMoreAlbums(false);
               setLoadingAlbums(false);
               return;
            }

            const ids = Array.from(approvedAlbumIds);
            setAllAlbumIds(ids);

            const firstBatch = ids.slice(0, ALBUM_PAGE_SIZE);
            const enriched = await enrichAlbumBatch(firstBatch);

            setAccessibleAlbums(enriched);
            setHasMoreAlbums(ids.length > ALBUM_PAGE_SIZE);
         } catch (error) {
            console.error('Error fetching accessible albums:', error);
            setMembershipAlbumsForFeed(null);
         } finally {
            setLoadingAlbums(false);
         }
      };

      fetchAccessibleAlbums();
   }, [currentUser, albumsRefreshTrigger, enrichAlbumBatch, fetchUserDetails]);

   // Load more accessible albums when user scrolls to sentinel
   const loadMoreAlbums = useCallback(async () => {
      if (loadingMoreAlbums || !hasMoreAlbums) return;
      setLoadingMoreAlbums(true);
      try {
         const currentCount = accessibleAlbums.length;
         const nextBatch = allAlbumIds.slice(currentCount, currentCount + ALBUM_PAGE_SIZE);
         const enriched = await enrichAlbumBatch(nextBatch);
         setAccessibleAlbums((prev) => [...prev, ...enriched]);
         setHasMoreAlbums(currentCount + ALBUM_PAGE_SIZE < allAlbumIds.length);
      } catch (error) {
         console.error('Error loading more albums:', error);
      } finally {
         setLoadingMoreAlbums(false);
      }
   }, [loadingMoreAlbums, hasMoreAlbums, accessibleAlbums.length, allAlbumIds, enrichAlbumBatch]);

   // IntersectionObserver for infinite scroll on accessible albums
   useEffect(() => {
      if (!albumSentinelRef.current || !hasMoreAlbums) return;
      const observer = new IntersectionObserver(
         ([entry]) => {
            if (entry.isIntersecting) loadMoreAlbums();
         },
         { rootMargin: '200px' },
      );
      observer.observe(albumSentinelRef.current);
      return () => observer.disconnect();
   }, [hasMoreAlbums, loadMoreAlbums]);

   // Process activities helper function
   const processActivities = useCallback(async (items: any[]) => {
      // Resolve actor IDs and fetch user profiles
      const userProfiles: Record<string, { displayName: string; photoURL: string }> = {};
      const enrichedItems = await Promise.all(
         items.map(async (item) => {
            try {
               let actorId = item.actorId;
               console.log('🔍 Processing activity:', {
                  id: item.id,
                  action: item.action,
                  actorId,
                  contextId: item.contextId,
               });

               // If no actorId, try to get it from related collections
               if (!actorId && item.contextId) {
                  // For access requests, check feed collection for sender info
                  if (item.action === 'access_request') {
                     const feedQuery = query(
                        collection(db, 'feed'),
                        where('waitlistId', '==', item.contextId),
                        limit(1),
                     );
                     const feedSnapshot = await getDocs(feedQuery);
                     if (!feedSnapshot.empty) {
                        const feedData = feedSnapshot.docs[0].data();
                        actorId = feedData.senderid;
                        console.log('✅ Found actorId from feed:', actorId);
                     }
                  } else if (
                     item.action === 'approved_request' ||
                     item.action === 'denied_request'
                  ) {
                     // For approvals/denials, the actor is the album owner
                     actorId = item.userId;
                     console.log('✅ Using userId as actorId:', actorId);
                  }
               }

               // Fetch user profile if we have an actorId
               if (actorId && !userProfiles[actorId]) {
                  const userDoc = await getDoc(doc(db, 'users', actorId));
                  if (userDoc.exists()) {
                     const userData = userDoc.data();
                     userProfiles[actorId] = {
                        displayName:
                           userData.displayName || userData.email?.split('@')[0] || 'User',
                        photoURL: userData.photoURL || '',
                     };
                     console.log('✅ Fetched user profile:', userProfiles[actorId]);
                  } else {
                     console.log('⚠️ User document not found for:', actorId);
                  }
               }

               return { ...item, resolvedActorId: actorId };
            } catch (error) {
               console.error(`Error fetching actor for activity ${item.id}:`, error);
               return item;
            }
         }),
      );

      console.log('🎯 Final userProfiles:', userProfiles);
      console.log(
         '🎯 Enriched items:',
         enrichedItems.map((i) => ({ id: i.id, resolvedActorId: i.resolvedActorId })),
      );

      // Resolve album covers: use albumCoverUrl from activity data, fetch missing ones from Firestore
      const covers: Record<string, string> = {};
      const missingAlbumIds: string[] = [];

      enrichedItems.forEach((item) => {
         if (!item.albumId) return;
         if (item.albumCoverUrl) {
            covers[item.albumId] = item.albumCoverUrl;
         } else if (!covers[item.albumId]) {
            missingAlbumIds.push(item.albumId);
         }
      });

      // Deduplicate and fetch missing covers
      const uniqueMissing = Array.from(new Set(missingAlbumIds));
      if (uniqueMissing.length > 0) {
         await Promise.all(
            uniqueMissing.map(async (albumId) => {
               try {
                  const albumDoc = await getDoc(doc(db, 'albums', albumId));
                  if (albumDoc.exists()) {
                     const data = albumDoc.data();
                     const url = data.coverPhotoUrl || data.coverPhoto || '';
                     if (url) covers[albumId] = url;
                  }
               } catch (_) {}
            })
         );
      }

      setActivityAlbumCovers(covers);
      setRecentActivities(enrichedItems);
      setActivityUsers(userProfiles);
      setLoadingRecentActivities(false);
   }, []);

   // Fetch activities where user is either the owner OR the actor
   const fetchActivities = useCallback(async () => {
      if (!currentUser) {
         setRecentActivities([]);
         setLoadingRecentActivities(false);
         return;
      }

      setLoadingRecentActivities(true);
      try {
         console.log('🔄 Fetching activities for user:', currentUser.uid);

         // Query 1: Activities where user is the owner (userId)
         const ownerQuery = query(
            collection(db, 'feed_recent_activity'),
            where('userId', '==', currentUser.uid),
            limit(25),
         );

         // Query 2: Activities where user is the actor (actorId)
         const actorQuery = query(
            collection(db, 'feed_recent_activity'),
            where('actorId', '==', currentUser.uid),
            limit(25),
         );

         const [ownerSnapshot, actorSnapshot] = await Promise.all([
            getDocs(ownerQuery),
            getDocs(actorQuery),
         ]);

         console.log('📊 Owner activities found:', ownerSnapshot.size);
         console.log('📊 Actor activities found:', actorSnapshot.size);

         // Merge and deduplicate activities
         const activitiesMap = new Map();

         ownerSnapshot.docs.forEach((doc) => {
            const data = doc.data();
            console.log('📄 Owner activity:', {
               id: doc.id,
               action: data.action,
               userId: data.userId,
               actorId: data.actorId,
            });
            activitiesMap.set(doc.id, {
               id: doc.id,
               ...(data as any),
            });
         });

         actorSnapshot.docs.forEach((doc) => {
            if (!activitiesMap.has(doc.id)) {
               const data = doc.data();
               console.log('📄 Actor activity:', {
                  id: doc.id,
                  action: data.action,
                  userId: data.userId,
                  actorId: data.actorId,
               });
               activitiesMap.set(doc.id, {
                  id: doc.id,
                  ...(data as any),
               });
            }
         });

         const items = Array.from(activitiesMap.values())
            .sort((a, b) => {
               const aTime = a.createdAt?.toMillis ? a.createdAt.toMillis() : 0;
               const bTime = b.createdAt?.toMillis ? b.createdAt.toMillis() : 0;
               return bTime - aTime;
            })
            .slice(0, 15);

         console.log('✅ Total merged activities:', items.length);

         // Process activities (same as before)
         await processActivities(items);
      } catch (error) {
         console.error('❌ Error fetching recent activities', error);
         setRecentActivities([]);
         setLoadingRecentActivities(false);
      }
   }, [currentUser, processActivities]);

   // Listen for recent album activities for this user
   useEffect(() => {
      if (!currentUser) {
         return;
      }

      // Initial fetch only - no polling
      fetchActivities();
   }, [currentUser, fetchActivities]);

   // Fetch top albums for sidebar (albums user is member of or invited to, sorted by photo count)
   useEffect(() => {
      const fetchTopAlbums = async () => {
         if (!currentUser) {
            setTopAlbums([]);
            setLoadingTopAlbums(false);
            return;
         }

         setLoadingTopAlbums(true);
         try {
            const userEmail = currentUser.email?.toLowerCase() || '';
            const albumsMap = new Map<string, TopAlbum>();

            // 1. Fetch albums where user is a member (from albums collection)
            const headers = await getAuthHeaders();
            const memberAlbumsResponse = await fetch(
               `${process.env.REACT_APP_API_URL}/api/albums?userId=${currentUser.uid}`,
               { headers },
            );

            if (memberAlbumsResponse.ok) {
               const memberAlbumsData = await memberAlbumsResponse.json();
               const albums = memberAlbumsData.albums || [];

               albums.forEach((album: any) => {
                  // Check if user is actually a member or owner
                  const isOwner = album.createdBy === currentUser.uid;
                  const isMember = album.members?.some(
                     (m: any) =>
                        m.userId === currentUser.uid || m.email?.toLowerCase() === userEmail,
                  );

                  if (isOwner || isMember) {
                     albumsMap.set(album.id, {
                        id: album.id,
                        title: album.title || 'Untitled Album',
                        photoCount: album.photoCount || album.photoIds?.length || 0,
                        coverUrl: album.coverPhotoUrl || album.coverPhoto || '',
                        status: 'member',
                     });
                  }
               });
            }

            // 2. Fetch pending invitations (invited but not yet accepted)
            const feedRef = collection(db, 'feed');
            const invitedQuery = query(
               feedRef,
               where('card_type', '==', 'album_invitation_received'),
               where('recipientId', '==', currentUser.uid),
               where('status', '==', 'pending'),
               where('seen', '==', false),
               limit(20),
            );

            const invitedSnapshot = await getDocs(invitedQuery);
            invitedSnapshot.forEach((docSnap) => {
               const data = docSnap.data();
               if (data.albumId && !albumsMap.has(data.albumId)) {
                  albumsMap.set(data.albumId, {
                     id: data.albumId,
                     title: data.albumTitle || 'Untitled Album',
                     photoCount: data.albumPhotoCount || 0,
                     coverUrl: data.albumCoverUrl || '',
                     status: 'invited',
                  });
               }
            });

            // Convert to array and sort by photo count (descending)
            const sortedAlbums = Array.from(albumsMap.values())
               .sort((a, b) => b.photoCount - a.photoCount)
               .slice(0, 5); // Top 5 albums

            setTopAlbums(sortedAlbums);
         } catch (error) {
            console.error('Error fetching top albums:', error);
            setTopAlbums([]);
         } finally {
            setLoadingTopAlbums(false);
         }
      };

      fetchTopAlbums();
   }, [currentUser, albumsRefreshTrigger, receivedInvitations]);

   // Fetch quick stats for sidebar
   useEffect(() => {
      const fetchQuickStats = async () => {
         if (!currentUser) {
            setQuickStats({ totalPhotos: 0, albumInvites: 0, eventsCount: 0 });
            setLoadingQuickStats(false);
            return;
         }

         setLoadingQuickStats(true);
         try {
            const userEmail = currentUser.email?.toLowerCase() || '';
            let totalPhotos = 0;
            let albumInvites = 0;
            let eventsCount = 0;

            // 1. Count total photos from albums user is a member of
            const headers = await getAuthHeaders();
            const memberAlbumsResponse = await fetch(
               `${process.env.REACT_APP_API_URL}/api/albums?userId=${currentUser.uid}`,
               { headers },
            );

            if (memberAlbumsResponse.ok) {
               const memberAlbumsData = await memberAlbumsResponse.json();
               const albums = memberAlbumsData.albums || [];

               albums.forEach((album: any) => {
                  const isOwner = album.createdBy === currentUser.uid;
                  const isMember = album.members?.some(
                     (m: any) =>
                        m.userId === currentUser.uid || m.email?.toLowerCase() === userEmail,
                  );

                  if (isOwner || isMember) {
                     totalPhotos += album.photoCount || album.photoIds?.length || 0;
                  }
               });
            }

            // 2. Count pending album invitations
            const feedRef = collection(db, 'feed');
            const invitedQuery = query(
               feedRef,
               where('card_type', '==', 'album_invitation_received'),
               where('recipientId', '==', currentUser.uid),
               where('status', '==', 'pending'),
               where('seen', '==', false),
               limit(20),
            );

            const invitedSnapshot = await getDocs(invitedQuery);
            invitedSnapshot.forEach((docSnap) => {
               const data = docSnap.data();
               albumInvites++;
               totalPhotos += data.albumPhotoCount || 0;
            });

            // 3. Count events created by this user
            const eventsQuery = query(
               collection(db, 'events'),
               where('createdBy', '==', currentUser.uid),
               limit(50),
            );
            const eventsSnapshot = await getDocs(eventsQuery);
            eventsCount = eventsSnapshot.size;

            setQuickStats({ totalPhotos, albumInvites, eventsCount });
         } catch (error) {
            console.error('Error fetching quick stats:', error);
            setQuickStats({ totalPhotos: 0, albumInvites: 0, eventsCount: 0 });
         } finally {
            setLoadingQuickStats(false);
         }
      };

      fetchQuickStats();
   }, [currentUser, albumsRefreshTrigger, receivedInvitations]);

   const handleApprove = async (requestId: string, albumId: string, requesterId: string) => {
      try {
         // Add user to approving set
         setApprovingUsers((prev) => new Set(prev).add(requestId));

         // Find the waitlistId from the feed card
         const feedCardSnapshot = await getDocs(
            query(collection(db, 'feed'), where('__name__', '==', requestId)),
         );

         let waitlistId = requestId;
         let feedCardData: any = null;
         if (!feedCardSnapshot.empty) {
            feedCardData = feedCardSnapshot.docs[0].data();
            waitlistId = feedCardData.waitlistId || requestId;
         }

         // Get auth headers
         const headers = await getAuthHeaders();
         headers['Content-Type'] = 'application/json';

         const response = await fetch(
            `${process.env.REACT_APP_API_URL}/api/albums/${albumId}/waitlist/${waitlistId}/approve`,
            {
               method: 'POST',
               headers,
               body: JSON.stringify({ userId: requesterId }),
            },
         );

         const result = await response.json();

         if (result.success) {
            setSnackbarMessage('User approved successfully');
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
            await logAlbumActivity({
               action: 'approved_request',
               albumId,
               albumTitle: feedCardData?.albumTitle,
               contextId: requestId,
               details: `Approved access request for ${feedCardData?.albumTitle || 'album'}`,
            });
            // The feed will auto-refresh via onSnapshot listener
         } else {
            setSnackbarMessage(result.error || 'Failed to approve user');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
         }
      } catch (err) {
         console.error('Error approving user:', err);
         setSnackbarMessage('Failed to approve user');
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
      } finally {
         // Remove user from approving set
         setApprovingUsers((prev) => {
            const newSet = new Set(prev);
            newSet.delete(requestId);
            return newSet;
         });
      }
   };

   const handleDeny = async (requestId: string) => {
      try {
         // Add user to rejecting set
         setRejectingUsers((prev) => new Set(prev).add(requestId));

         // Get the feed card to extract albumId and waitlistId
         const feedCardSnapshot = await getDocs(
            query(collection(db, 'feed'), where('__name__', '==', requestId)),
         );

         if (feedCardSnapshot.empty) {
            throw new Error('Feed card not found');
         }

         const feedCardData = feedCardSnapshot.docs[0].data();
         const albumId = feedCardData.albumId;
         const waitlistId = feedCardData.waitlistId || requestId;

         // Get auth headers
         const headers = await getAuthHeaders();
         headers['Content-Type'] = 'application/json';

         const response = await fetch(
            `${process.env.REACT_APP_API_URL}/api/albums/${albumId}/waitlist/${waitlistId}/reject`,
            {
               method: 'POST',
               headers,
            },
         );

         const result = await response.json();

         if (result.success) {
            setSnackbarMessage('User rejected');
            setSnackbarSeverity('success');
            setSnackbarOpen(true);
            await logAlbumActivity({
               action: 'denied_request',
               albumId,
               albumTitle: feedCardData?.albumTitle,
               contextId: requestId,
               details: `Denied access request for ${feedCardData?.albumTitle || 'album'}`,
            });
            // The feed will auto-refresh via onSnapshot listener
         } else {
            setSnackbarMessage(result.error || 'Failed to reject user');
            setSnackbarSeverity('error');
            setSnackbarOpen(true);
         }
      } catch (err) {
         console.error('Error rejecting user:', err);
         setSnackbarMessage('Failed to reject user');
         setSnackbarSeverity('error');
         setSnackbarOpen(true);
      } finally {
         // Remove user from rejecting set
         setRejectingUsers((prev) => {
            const newSet = new Set(prev);
            newSet.delete(requestId);
            return newSet;
         });
      }
   };

   // Handler for when an invitation is resent successfully
   const handleResendSuccess = (batchId: string) => {
      setSnackbarMessage('Invitations resent successfully!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      logAlbumActivity({
         action: 'resend_invite',
         contextId: batchId,
         details: 'Resent album invitations',
      });
      // The feed will auto-refresh via onSnapshot listener
   };

   // Handler for when an invitation is cancelled successfully
   const handleCancelSuccess = (batchId: string) => {
      // Remove the invitation from the local state immediately for smooth UX
      setSentInvitations((prev) => prev.filter((inv) => (inv.batchId || inv.id) !== batchId));
      setSnackbarMessage('Invitations cancelled');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      logAlbumActivity({
         action: 'cancel_invite',
         contextId: batchId,
         details: 'Cancelled album invitations',
      });
   };

   // Handler for when a received invitation is accepted
   const handleAcceptSuccess = (invitationId: string) => {
      setReceivedInvitations((prev) => prev.filter((inv) => inv.invitationId !== invitationId));
      setSnackbarMessage('Invitation accepted! You now have access to this album.');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);

      // Trigger refresh of accessible albums to show the newly accessible album
      setTimeout(() => {
         setAlbumsRefreshTrigger((prev) => prev + 1);
      }, 1000); // Small delay to ensure server has processed the acceptance
   };

   // Handler for when a received invitation is declined
   const handleDeclineSuccess = (invitationId: string) => {
      setReceivedInvitations((prev) => prev.filter((inv) => inv.invitationId !== invitationId));
      setSnackbarMessage('Invitation declined');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
   };

   // Handler for viewing album from invitation card
   const handleViewAlbum = (albumId: string) => {
      logAlbumActivity({
         action: 'view_album',
         albumId,
         details: 'Viewed album from feed',
      });
      navigate(`/albums/${albumId}`);
   };

   const handleFeedUpload = useCallback(async (albumId: string, files: FileList | null) => {
      if (!files || files.length === 0) return;
      setUploadingAlbumId(albumId);
      try {
         const headers = await getAuthHeaders();
         const formData = new FormData();
         Array.from(files).forEach((file) => formData.append('photos', file));

         const API = process.env.REACT_APP_API_URL || '';
         const res = await fetch(`${API}/api/albums/${albumId}/upload-photos`, {
            method: 'POST',
            headers: { Authorization: headers.Authorization },
            body: formData,
         });

         const body = await res.json().catch(() => null);
         if (!res.ok) {
            const msg = body?.message || 'Upload failed';
            setSnackbarSeverity('error');
            setSnackbarMessage(msg);
            setSnackbarOpen(true);
            return;
         }

         setSnackbarSeverity('success');
         setSnackbarMessage(
            `${files.length} photo${files.length > 1 ? 's' : ''} uploaded successfully!`,
         );
         setSnackbarOpen(true);
         setAlbumsRefreshTrigger((prev) => prev + 1);
      } catch (err: any) {
         setSnackbarSeverity('error');
         setSnackbarMessage(err?.message || 'Upload failed');
         setSnackbarOpen(true);
      } finally {
         setUploadingAlbumId(null);
         const input = fileInputRefs.current[albumId];
         if (input) input.value = '';
      }
   }, []);

   // Handle profile edit dialog
   const handleOpenEditProfile = () => {
      setEditProfileDialogOpen(true);
   };

   const handleCloseEditProfile = () => {
      setEditProfileDialogOpen(false);
   };

   const handleCloseActivityPopup = () => {
      setSelectedActivity(null);
      setActivityPopupType(null);
      setSelectedUserForPopup(null);
   };

   const handleActivityRowClick = useCallback(async (activity: ActivityItem) => {
      const userPopupActions = [
         'access_request',
         'view_album',
         'approved_request',
         'denied_request',
      ];
      const isUserPopup = userPopupActions.includes(activity.action);
      const isAlbumPopup = !isUserPopup && (!!activity.albumId || !!activity.albumTitle);

      if (isUserPopup) {
         setSelectedActivity(activity);
         setActivityPopupType('user');
         setLoadingActivityUser(true);
         setSelectedUserForPopup(null);
         const uid =
            activity.action === 'approved_request' || activity.action === 'denied_request'
               ? activity.targetUserId
               : activity.resolvedActorId;
         if (uid) {
            try {
               const userDoc = await getDoc(doc(db, 'users', uid));
               if (userDoc.exists()) {
                  const d = userDoc.data();
                  setSelectedUserForPopup({
                     userId: uid,
                     email: d.email || '',
                     displayName: d.displayName ?? d.email?.split('@')[0] ?? 'User',
                     photoURL: d.photoURL || '',
                     ...d,
                  });
               } else {
                  setSelectedUserForPopup({
                     userId: uid,
                     email: '',
                     displayName: activity.targetUserDisplayName ?? 'User',
                     photoURL: activity.targetUserPhotoURL ?? '',
                  });
               }
            } catch (err) {
               setSelectedUserForPopup({
                  userId: uid,
                  email: '',
                  displayName: activity.targetUserDisplayName ?? 'User',
                  photoURL: activity.targetUserPhotoURL ?? '',
               });
            }
         } else {
            setSelectedUserForPopup({
               userId: undefined,
               email: '',
               displayName: activity.targetUserDisplayName ?? 'Unknown',
               photoURL: activity.targetUserPhotoURL ?? '',
            });
         }
         setLoadingActivityUser(false);
      } else if (isAlbumPopup) {
         setSelectedActivity(activity);
         setActivityPopupType('album');
      }
   }, []);

   const handleProfileUpdated = async (updatedData: any) => {
      // Update local profile data
      setProfileData(updatedData);

      // Re-check profile completion status
      if (currentUser) {
         try {
            const status = await checkProfileCompletion(
               currentUser.uid,
               updatedData.displayName || currentUser.displayName,
               updatedData.photoURL || currentUser.photoURL,
            );
            setProfileStatus(status);
         } catch (error) {
            console.error('Error re-checking profile completion:', error);
         }
      }

      // Close dialog
      handleCloseEditProfile();

      // Show success message
      setSnackbarMessage('Profile updated successfully!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
   };

   const getTimeAgo = (timestamp: Timestamp) => {
      const now = Date.now();
      const diff = now - timestamp.toMillis();
      const minutes = Math.floor(diff / 60000);
      const hours = Math.floor(diff / 3600000);
      const days = Math.floor(diff / 86400000);

      if (minutes < 1) return 'Just now';
      if (minutes === 1) return '1 minute ago';
      if (minutes < 60) return `${minutes} minutes ago`;
      if (hours === 1) return '1 hour ago';
      if (hours < 24) return `${hours} hours ago`;
      if (days === 1) return '1 day ago';
      return `${days} days ago`;
   };

   const getAccessMethodIcon = (method: string) => {
      switch (method) {
         case 'qr':
            return <QrCodeScannerIcon sx={{ fontSize: 14, color: theme.palette.warning.main }} />;
         case 'link':
            return <LinkIcon sx={{ fontSize: 14, color: theme.palette.info.main }} />;
         default:
            return <LinkIcon sx={{ fontSize: 14, color: theme.palette.info.main }} />;
      }
   };

   const getAccessMethodText = (method: string) => {
      switch (method) {
         case 'qr':
            return 'Via QR Code Scan';
         case 'link':
            return 'Via Share Link';
         default:
            return 'Via Share Link';
      }
   };

   const quickActions = useMemo(
      () => [
         {
            id: 'event',
            title: 'Create Your First Event',
            subtitle: 'Start building your social calendar',
            description:
               'Events help you organize gatherings, track RSVPs, and share photos with attendees. Create your first event to get started!',
            features: [
               'Send invitations and track RSVPs',
               'Share event photos and memories',
               'Generate QR codes for easy sharing',
            ],
            icon: EventIcon,
            iconBg: theme.palette.info.main + '20',
            iconColor: theme.palette.info.main,
            borderColor: theme.palette.info.main + '40',
            buttonColor: theme.palette.info.main,
            route: '/events/create',
         },
         {
            id: 'album',
            title: 'Create Your First Album',
            subtitle: 'Share photos with friends and family',
            description:
               'Albums let you organize and share photos from your events and trips. Choose from private, public, or paid album options.',
            features: [
               'Private, public, or paid album types',
               'Easy photo sharing and access control',
               'Track views and engagement',
            ],
            icon: PhotoAlbumIcon,
            iconBg: theme.palette.secondary.main + '20',
            iconColor: theme.palette.secondary.main,
            borderColor: theme.palette.secondary.main + '40',
            buttonColor: theme.palette.secondary.main,
            route: '/albums',
         },
      ],
      [theme],
   );

   const handleFeedPhotographerRegistered = useCallback(() => {
      setPhotographerEligibilityRefreshKey((k) => k + 1);
      setSnackbarSeverity('success');
      setSnackbarMessage("You're set up as a photographer. Open MyKiosk when you're ready.");
      setSnackbarOpen(true);
   }, []);

   const handleOpenMyKioskFromPromo = useCallback((url: string) => {
      if (!url) {
         setSnackbarSeverity('warning');
         setSnackbarMessage('Photographer website is not configured yet.');
         setSnackbarOpen(true);
         return;
      }
      window.open(url, '_blank', 'noopener,noreferrer');
   }, []);

   /**
    * Header CTA: same row as "Activity".
    * Show while loading OR confirmed eligible — avoids blank header when Firestore is slow.
    * Hide only when confirmed ineligible (photographer or organization account), from profile like public album.
    */
   const showPhotographerHeaderPromo = !!currentUser && photographerEligibility !== 'ineligible';

   // Combine and sort all feed cards by date (must be before early returns)
   const sortedFeedCards = useMemo(() => {
      type FeedCard = {
         id: string;
         type:
            | 'linkClick'
            | 'download'
            | 'albumCreated'
            | 'eventCreated'
            | 'invitationReceived'
            | 'invitationSent'
            | 'accessRequest'
            | 'photographer'
            | 'sponsor';
         timestamp: any;
         data:
            | AlbumLinkClick
            | AlbumDownload
            | AlbumCreated
            | EventCreated
            | AlbumInvitationReceived
            | AlbumInvitationSent
            | AlbumAccessRequest
            | PhotographerFeedCardData
            | SponsorCardData;
      };

      const allCards: FeedCard[] = [
         ...albumLinkClicks.map((card) => ({
            id: card.id,
            type: 'linkClick' as const,
            timestamp: card.clickedAt,
            data: card,
         })),
         ...albumDownloads.map((card) => ({
            id: card.id,
            type: 'download' as const,
            timestamp: card.lastDownloadAt,
            data: card,
         })),
         ...mergedAlbumFeedCards.map((card) => ({
            id: card.id,
            type: 'albumCreated' as const,
            timestamp: card.createdAt,
            data: card,
         })),
         ...eventsCreated.map((card) => ({
            id: card.id,
            type: 'eventCreated' as const,
            timestamp: card.createdAt,
            data: card,
         })),
         ...receivedInvitations.map((card) => ({
            id: card.id,
            type: 'invitationReceived' as const,
            timestamp: card.sentAt,
            data: card,
         })),
         ...sentInvitations.map((card) => ({
            id: card.id,
            type: 'invitationSent' as const,
            timestamp: card.sentAt,
            data: card,
         })),
         ...accessRequests.map((card) => ({
            id: card.id,
            type: 'accessRequest' as const,
            timestamp: card.timestamp,
            data: card,
         })),
         ...photographerCards.map((card) => ({
            id: `photographer-${card.userId}`,
            type: 'photographer' as const,
            timestamp: card.lastActivityAt,
            data: card,
         })),
      ];

      // Sort by timestamp (most recent first)
      const sorted = allCards.sort((a, b) => {
         const toMillis = (value: any): number => {
            if (!value) return 0;
            if (value instanceof Date) return value.getTime();
            if (typeof value.toDate === 'function') return value.toDate().getTime();
            if (typeof value.seconds === 'number') return value.seconds * 1000;
            return 0;
         };

         const aTime = toMillis(a.timestamp);
         const bTime = toMillis(b.timestamp);
         return bTime - aTime; // Most recent first
      });

      if (activeFilter === 'hot') {
         return sponsorCards
            .filter((sc) => sc.placement?.showInHotTab)
            .map((sc) => ({
               id: `sponsor-${sc.id}`,
               type: 'sponsor' as const,
               timestamp: new Date(),
               data: sc as SponsorCardData,
            }));
      }

      const filterMap: Record<string, (card: FeedCard) => boolean> = {
         all: () => true,
         photos: (card) => card.type === 'albumCreated',
         analytics: (card) => card.type === 'linkClick' || card.type === 'download',
         notifications: (card) =>
            card.type === 'accessRequest' ||
            card.type === 'invitationReceived' ||
            card.type === 'invitationSent' ||
            card.type === 'eventCreated' ||
            card.type === 'photographer',
      };
      const filtered = sorted.filter(filterMap[activeFilter] || filterMap.all);

      const result = [...filtered];
      sponsorCards.forEach((sc) => {
         const pos = sc.placement?.position;
         let idx = result.length;
         if (pos === 'top') idx = 0;
         else if (pos === 'after_first') idx = Math.min(1, result.length);
         else if (pos === 'after_third') idx = Math.min(3, result.length);
         result.splice(idx, 0, {
            id: `sponsor-${sc.id}`,
            type: 'sponsor' as const,
            timestamp: new Date(),
            data: sc as SponsorCardData,
         });
      });
      return result;
   }, [
      albumLinkClicks,
      albumDownloads,
      mergedAlbumFeedCards,
      eventsCreated,
      receivedInvitations,
      sentInvitations,
      accessRequests,
      photographerCards,
      activeFilter,
      sponsorCards,
   ]);

   const visibleFeedCards = useMemo(
      () => sortedFeedCards.slice(0, visibleFeedCardCount),
      [sortedFeedCards, visibleFeedCardCount],
   );

   useEffect(() => {
      setVisibleFeedCardCount((prev) => {
         const len = sortedFeedCards.length;
         if (len === 0) return FEED_CARDS_INITIAL;
         return Math.min(prev, len);
      });
   }, [sortedFeedCards.length]);

   useEffect(() => {
      const el = feedLoadMoreSentinelRef.current;
      if (!el || visibleFeedCardCount >= sortedFeedCards.length) return;

      const obs = new IntersectionObserver(
         (entries) => {
            if (!entries.some((e) => e.isIntersecting)) return;
            setVisibleFeedCardCount((n) => {
               const max = sortedFeedCards.length;
               if (n >= max) return n;
               return Math.min(n + FEED_CARDS_PAGE, max);
            });
         },
         { root: null, rootMargin: '200px', threshold: 0 },
      );
      obs.observe(el);
      return () => obs.disconnect();
   }, [sortedFeedCards, visibleFeedCardCount]);

   const filterCounts = useMemo(
      () => ({
         all:
            accessRequests.length +
            albumLinkClicks.length +
            albumDownloads.length +
            mergedAlbumFeedCards.length +
            eventsCreated.length +
            receivedInvitations.length +
            sentInvitations.length +
            photographerCards.length,
         photos: allAlbumIds.length,
         analytics: albumLinkClicks.length + albumDownloads.length,
         notifications:
            accessRequests.length +
            receivedInvitations.length +
            sentInvitations.length +
            eventsCreated.length +
            photographerCards.length,
         hot: sponsorCards.filter((sc) => sc.placement?.showInHotTab).length,
      }),
      [
         accessRequests,
         albumLinkClicks,
         albumDownloads,
         albumsCreated,
         eventsCreated,
         receivedInvitations,
         sentInvitations,
         photographerCards,
         accessibleAlbums,
         sponsorCards,
      ],
   );

   // Load photographer profile when a photographer card is opened
   useEffect(() => {
      if (!profilePopoverUserId) {
         setProfilePopoverData(null);
         return;
      }
      let cancelled = false;
      setProfilePopoverLoading(true);
      setProfilePopoverData(null);
      fetch(`${process.env.REACT_APP_API_URL}/api/profile/public/${profilePopoverUserId}`)
         .then((res) => (res.ok ? res.json() : null))
         .then((data) => {
            if (!cancelled && data) setProfilePopoverData(data);
         })
         .catch(() => {
            if (!cancelled) setProfilePopoverData(null);
         })
         .finally(() => {
            if (!cancelled) setProfilePopoverLoading(false);
         });
      return () => {
         cancelled = true;
      };
   }, [profilePopoverUserId]);

   if (!currentUser) {
      return (
         <Container maxWidth='md' sx={{ py: 4 }}>
            <Typography>Please log in to view your feed.</Typography>
         </Container>
      );
   }

   return (
      <SubscriptionGuard redirectOnNoSubscription={false}>
         <Box
            sx={{
               minHeight: '100vh',
               backgroundColor: '#080A0F', // bgBase from theme
               position: 'relative',
               width: '100%',
            }}
         >
            <Box
               sx={{
                  py: { xs: 2, sm: 3 },
                  px: { xs: 0, sm: 0 },
                  maxWidth: { xs: '100%', lg: 'calc(100% - 440px)' },
                  width: '100%',
                  ml: 0,
                  boxSizing: 'border-box',
               }}
            >
               {/* Feed Title - At Top */}
               <Box
                  sx={{
                     mb: { xs: 1.5, sm: 3 },
                     px: { xs: 1.5, sm: 3 },
                     textAlign: { xs: 'left', lg: 'center' },
                  }}
               >
                  <Typography
                     variant='h4'
                     sx={{
                        fontFamily: '"Cormorant Garamond", serif',
                        fontWeight: 300,
                        color: theme.palette.text.primary,
                        mb: 0.5,
                        fontSize: { xs: '1.75rem', sm: '2.25rem' },
                     }}
                  >
                     Activity
                  </Typography>
                  <Typography
                     variant='body2'
                     color='text.secondary'
                     sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
                  >
                     Stay updated with access requests and notifications
                  </Typography>
               </Box>

               {/* Main Feed Column — centered on lg (sidebar visible) */}
               <Box
                  sx={{
                     display: 'flex',
                     justifyContent: { xs: 'flex-start', lg: 'center' },
                     position: 'relative',
                     px: { xs: 1.5, sm: 3 },
                  }}
               >
                  <Box sx={{ width: '100%', maxWidth: { xs: '100%', sm: '600px' }, pb: '70px' }}>
                     {/* Loading State */}
                     {(loading ||
                        loadingAlbums ||
                        loadingSentInvitations ||
                        loadingReceivedInvitations ||
                        loadingLinkClicks) && (
                        <Fade in={true}>
                           <Box sx={{ mb: 2 }}>
                              {/* Loading Indicator */}
                              <Box
                                 sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 2,
                                    py: 2,
                                    px: 3,
                                    backgroundColor: '#0E1118', // bgSurface from theme
                                    borderRadius: 2,
                                    boxShadow: 'none',
                                    border: '1px solid rgba(255, 255, 255, 0.07)', // border from theme
                                    mb: 2,
                                 }}
                              >
                                 <CircularProgress
                                    size={24}
                                    sx={{ color: theme.palette.primary.main }}
                                 />
                                 <Typography
                                    variant='body2'
                                    sx={{
                                       color: theme.palette.text.secondary,
                                       fontWeight: 500,
                                    }}
                                 >
                                    Loading your feed...
                                 </Typography>
                              </Box>

                              {/* Skeleton Cards */}
                              <Stack spacing={2}>
                                 {[1, 2, 3].map((i) => (
                                    <Card
                                       key={i}
                                       sx={{
                                          borderRadius: 2,
                                          boxShadow: 'none',
                                          border: '1px solid rgba(255, 255, 255, 0.07)', // border from theme
                                          backgroundColor: '#0E1118', // bgSurface from theme
                                          overflow: 'hidden',
                                       }}
                                    >
                                       <CardContent sx={{ p: 2 }}>
                                          <Box
                                             sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 2,
                                                mb: 2,
                                             }}
                                          >
                                             <Skeleton variant='circular' width={48} height={48} />
                                             <Box sx={{ flex: 1 }}>
                                                <Skeleton variant='text' width='60%' height={24} />
                                                <Skeleton variant='text' width='40%' height={18} />
                                             </Box>
                                          </Box>
                                          <Skeleton
                                             variant='rectangular'
                                             width='100%'
                                             height={120}
                                             sx={{ borderRadius: 1, mb: 2 }}
                                          />
                                          <Box sx={{ display: 'flex', gap: 1 }}>
                                             <Skeleton variant='rounded' width={100} height={36} />
                                             <Skeleton variant='rounded' width={100} height={36} />
                                          </Box>
                                       </CardContent>
                                    </Card>
                                 ))}
                              </Stack>
                           </Box>
                        </Fade>
                     )}

                     {/* Main Feed Content */}
                     {!loading && (
                        <Stack spacing={2}>
                           {/* Access Requests are now shown in the sorted feed cards above */}
                           {false && accessRequests.length > 0 && (
                              <Box>
                                 <Stack spacing={2}>
                                    {accessRequests.map((request) => {
                                       // Determine if current user is owner or sender
                                       const isOwner = request.albumOwnerId === currentUser?.uid;
                                       const isSender = request.requesterId === currentUser?.uid;

                                       // Determine which user details to show
                                       // If owner: show sender details
                                       // If sender: show owner details
                                       const displayUserId = isOwner
                                          ? request.requesterId
                                          : request.albumOwnerId;
                                       const displayUserData = userDetails[displayUserId];

                                       return (
                                          <Card
                                             key={request.id}
                                             sx={{
                                                borderRadius: 2,
                                                boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
                                                border: `1px solid ${theme.palette.divider}`,
                                                borderLeft: isSender
                                                   ? `4px solid ${theme.palette.info.main}`
                                                   : `4px solid ${theme.palette.warning.main}`,
                                                overflow: 'visible',
                                                backgroundColor: theme.palette.background.paper,
                                             }}
                                          >
                                             <CardContent
                                                sx={{
                                                   p: { xs: 1.5, sm: 2.5 },
                                                   '&:last-child': { pb: { xs: 1.5, sm: 2.5 } },
                                                }}
                                             >
                                                {/* Header */}
                                                <Box
                                                   sx={{
                                                      display: 'flex',
                                                      flexDirection: { xs: 'column', sm: 'row' },
                                                      justifyContent: 'space-between',
                                                      alignItems: {
                                                         xs: 'flex-start',
                                                         sm: 'center',
                                                      },
                                                      gap: { xs: 1, sm: 0 },
                                                      mb: 1.5,
                                                   }}
                                                >
                                                   <Box
                                                      sx={{
                                                         display: 'flex',
                                                         gap: { xs: 1, sm: 1.5 },
                                                         alignItems: 'center',
                                                      }}
                                                   >
                                                      <Box
                                                         sx={{
                                                            width: 40,
                                                            height: 40,
                                                            borderRadius: 1.5,
                                                            overflow: 'hidden',
                                                            border: albumDetails[request.albumId]
                                                               ?.coverPhoto
                                                               ? '2px solid #f59e0b'
                                                               : 'none',
                                                            backgroundColor: albumDetails[
                                                               request.albumId
                                                            ]?.coverPhoto
                                                               ? 'transparent'
                                                               : '#fef3c7',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                         }}
                                                      >
                                                         {albumDetails[request.albumId]
                                                            ?.coverPhoto ? (
                                                            // Show single cover photo
                                                            <img
                                                               src={
                                                                  albumDetails[request.albumId]
                                                                     .coverPhoto
                                                               }
                                                               alt={
                                                                  albumDetails[request.albumId]
                                                                     ?.title || request.albumName
                                                               }
                                                               style={{
                                                                  width: '100%',
                                                                  height: '100%',
                                                                  objectFit: 'cover',
                                                               }}
                                                            />
                                                         ) : (
                                                            // Show icon if no cover photo
                                                            <PhotoLibraryIcon
                                                               sx={{
                                                                  color: '#f59e0b',
                                                                  fontSize: 20,
                                                               }}
                                                            />
                                                         )}
                                                      </Box>
                                                      <Box>
                                                         <Typography
                                                            variant='subtitle1'
                                                            sx={{
                                                               fontWeight: 500,
                                                               color: theme.palette.text.primary,
                                                               fontSize: '0.95rem',
                                                            }}
                                                         >
                                                            Album Access Request
                                                         </Typography>
                                                         <Typography
                                                            variant='body2'
                                                            color='text.secondary'
                                                            sx={{ fontSize: '0.8rem' }}
                                                         >
                                                            {isOwner
                                                               ? 'Someone wants to view your photos'
                                                               : 'Your request is pending approval'}
                                                         </Typography>
                                                      </Box>
                                                   </Box>
                                                   <Chip
                                                      label={isSender ? 'Waiting' : 'Pending'}
                                                      size='small'
                                                      icon={
                                                         <AccessTimeIcon sx={{ fontSize: 14 }} />
                                                      }
                                                      sx={{
                                                         backgroundColor: isSender
                                                            ? theme.palette.info.main
                                                            : theme.palette.warning.main,
                                                         color: theme.palette.text.primary,
                                                         fontWeight: 500,
                                                         height: 24,
                                                         fontSize: '0.75rem',
                                                         '& .MuiChip-icon': {
                                                            color: theme.palette.text.primary,
                                                         },
                                                      }}
                                                   />
                                                </Box>

                                                {/* User Info (Requester for Owner, Owner for Sender) */}
                                                <Box
                                                   sx={{
                                                      display: 'flex',
                                                      alignItems: 'center',
                                                      gap: { xs: 1, sm: 1.5 },
                                                      my: { xs: 1.5, sm: 2 },
                                                   }}
                                                >
                                                   <Avatar
                                                      src={displayUserData?.photoURL || ''}
                                                      alt={
                                                         displayUserData?.displayName ||
                                                         (isOwner
                                                            ? request.requesterName
                                                            : 'Album Owner')
                                                      }
                                                      sx={{
                                                         width: { xs: 40, sm: 48 },
                                                         height: { xs: 40, sm: 48 },
                                                      }}
                                                   >
                                                      {(
                                                         displayUserData?.displayName ||
                                                         (isOwner
                                                            ? request.requesterName
                                                            : 'Album Owner')
                                                      ).charAt(0)}
                                                   </Avatar>
                                                   <Box sx={{ flex: 1 }}>
                                                      <Typography
                                                         variant='subtitle1'
                                                         sx={{
                                                            fontWeight: 500,
                                                            color: theme.palette.text.primary,
                                                            fontSize: '0.95rem',
                                                         }}
                                                      >
                                                         {displayUserData?.displayName ||
                                                            (isOwner
                                                               ? request.requesterName
                                                               : 'Album Owner')}
                                                      </Typography>
                                                      <Typography
                                                         variant='body2'
                                                         color='text.secondary'
                                                         sx={{ fontSize: '0.8rem' }}
                                                      >
                                                         {displayUserData?.email ||
                                                            (isOwner
                                                               ? request.requesterRole || 'Guest'
                                                               : 'Album Owner')}
                                                      </Typography>
                                                      <Box
                                                         sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 0.5,
                                                            mt: 0.25,
                                                         }}
                                                      >
                                                         {getAccessMethodIcon(request.accessMethod)}
                                                         <Typography
                                                            variant='caption'
                                                            color='text.secondary'
                                                            sx={{ fontSize: '0.75rem' }}
                                                         >
                                                            {getAccessMethodText(
                                                               request.accessMethod,
                                                            )}
                                                         </Typography>
                                                         <Typography
                                                            variant='caption'
                                                            color='text.secondary'
                                                            sx={{ fontSize: '0.75rem' }}
                                                         >
                                                            • {getTimeAgo(request.timestamp)}
                                                         </Typography>
                                                      </Box>
                                                   </Box>
                                                </Box>

                                                {/* Album Info */}
                                                <Box
                                                   sx={{
                                                      display: 'flex',
                                                      flexDirection: { xs: 'column', sm: 'row' },
                                                      alignItems: {
                                                         xs: 'flex-start',
                                                         sm: 'center',
                                                      },
                                                      justifyContent: 'space-between',
                                                      gap: { xs: 1, sm: 0 },
                                                      p: { xs: 1, sm: 1.5 },
                                                      backgroundColor:
                                                         theme.palette.error.main + '10',
                                                      borderRadius: 1.5,
                                                      mb: { xs: 1.5, sm: 2 },
                                                      border: `1px solid ${theme.palette.error.main}30`,
                                                   }}
                                                >
                                                   <Box
                                                      sx={{
                                                         display: 'flex',
                                                         alignItems: 'center',
                                                         gap: { xs: 1, sm: 1.5 },
                                                      }}
                                                   >
                                                      <Box
                                                         sx={{
                                                            width: { xs: 70, sm: 90 },
                                                            height: { xs: 50, sm: 60 },
                                                            position: 'relative',
                                                            flexShrink: 0,
                                                         }}
                                                      >
                                                         {albumDetails[request.albumId]?.photos
                                                            ?.length > 0 ? (
                                                            // Show stacked photos (max 4 for better visibility)
                                                            <>
                                                               {albumDetails[request.albumId].photos
                                                                  .slice(0, 4)
                                                                  .map((photoUrl, index) => (
                                                                     <Box
                                                                        key={index}
                                                                        sx={{
                                                                           width: {
                                                                              xs: 45,
                                                                              sm: 60,
                                                                           },
                                                                           height: {
                                                                              xs: 45,
                                                                              sm: 60,
                                                                           },
                                                                           borderRadius: 1.5,
                                                                           overflow: 'hidden',
                                                                           border: `2px solid ${theme.palette.secondary.main}60`,
                                                                           position: 'absolute',
                                                                           top: 0,
                                                                           left: {
                                                                              xs: index * 6,
                                                                              sm: index * 8,
                                                                           },
                                                                           backgroundColor:
                                                                              theme.palette
                                                                                 .background.paper,
                                                                           boxShadow:
                                                                              '0 2px 8px rgba(0,0,0,0.15)',
                                                                           zIndex: 4 - index,
                                                                        }}
                                                                     >
                                                                        <img
                                                                           src={photoUrl}
                                                                           alt={`Photo ${index + 1}`}
                                                                           style={{
                                                                              width: '100%',
                                                                              height: '100%',
                                                                              objectFit: 'cover',
                                                                           }}
                                                                        />
                                                                     </Box>
                                                                  ))}
                                                            </>
                                                         ) : (
                                                            // Show icon if no photos
                                                            <Box
                                                               sx={{
                                                                  width: { xs: 45, sm: 60 },
                                                                  height: { xs: 45, sm: 60 },
                                                                  backgroundColor:
                                                                     theme.palette.secondary.main +
                                                                     '20',
                                                                  borderRadius: 1.5,
                                                                  display: 'flex',
                                                                  alignItems: 'center',
                                                                  justifyContent: 'center',
                                                                  border: `2px solid ${theme.palette.secondary.main}60`,
                                                               }}
                                                            >
                                                               <PhotoLibraryIcon
                                                                  sx={{
                                                                     color: theme.palette.secondary
                                                                        .main,
                                                                     fontSize: { xs: 20, sm: 24 },
                                                                  }}
                                                               />
                                                            </Box>
                                                         )}
                                                      </Box>
                                                      <Box>
                                                         <Typography
                                                            variant='body1'
                                                            sx={{
                                                               fontWeight: 500,
                                                               color: theme.palette.text.primary,
                                                               fontSize: '0.9rem',
                                                            }}
                                                         >
                                                            {albumDetails[request.albumId]?.title ||
                                                               request.albumName}
                                                         </Typography>
                                                         <Typography
                                                            variant='caption'
                                                            color='text.secondary'
                                                            sx={{ fontSize: '0.75rem' }}
                                                         >
                                                            {albumDetails[request.albumId]
                                                               ?.photoCount ??
                                                               request.albumPhotoCount}{' '}
                                                            photos
                                                         </Typography>
                                                      </Box>
                                                   </Box>
                                                   {/* Only show View button for owners */}
                                                   {isOwner && (
                                                      <Button
                                                         size='small'
                                                         endIcon={<Box component='span'>›</Box>}
                                                         sx={{
                                                            color: theme.palette.info.main,
                                                            textTransform: 'none',
                                                            fontWeight: 500,
                                                            fontSize: '0.85rem',
                                                            minWidth: 'auto',
                                                            px: 1,
                                                         }}
                                                         onClick={() =>
                                                            handleViewAlbum(request.albumId)
                                                         }
                                                      >
                                                         View
                                                      </Button>
                                                   )}
                                                </Box>

                                                {/* Actions - Only show for owners */}
                                                {isOwner && (
                                                   <Box
                                                      sx={{
                                                         display: 'flex',
                                                         flexDirection: { xs: 'column', sm: 'row' },
                                                         gap: { xs: 1, sm: 1.5 },
                                                      }}
                                                   >
                                                      <Button
                                                         variant='outlined'
                                                         fullWidth
                                                         startIcon={
                                                            <CancelIcon
                                                               sx={{ fontSize: { xs: 16, sm: 18 } }}
                                                            />
                                                         }
                                                         onClick={() => handleDeny(request.id)}
                                                         disabled={
                                                            rejectingUsers.has(request.id) ||
                                                            approvingUsers.has(request.id)
                                                         }
                                                         sx={{
                                                            borderColor: theme.palette.divider,
                                                            color: theme.palette.error.main,
                                                            textTransform: 'none',
                                                            fontWeight: 500,
                                                            py: { xs: 1, sm: 0.75 },
                                                            fontSize: {
                                                               xs: '0.8rem',
                                                               sm: '0.875rem',
                                                            },
                                                            '&:hover': {
                                                               borderColor:
                                                                  theme.palette.error.main,
                                                               backgroundColor:
                                                                  theme.palette.error.main + '10',
                                                            },
                                                         }}
                                                      >
                                                         {rejectingUsers.has(request.id)
                                                            ? 'Denying...'
                                                            : 'Deny'}
                                                      </Button>
                                                      <Button
                                                         variant='contained'
                                                         fullWidth
                                                         startIcon={
                                                            <CheckCircleIcon
                                                               sx={{ fontSize: { xs: 16, sm: 18 } }}
                                                            />
                                                         }
                                                         onClick={() =>
                                                            handleApprove(
                                                               request.id,
                                                               request.albumId,
                                                               request.requesterId,
                                                            )
                                                         }
                                                         disabled={
                                                            approvingUsers.has(request.id) ||
                                                            rejectingUsers.has(request.id)
                                                         }
                                                         sx={{
                                                            backgroundColor:
                                                               theme.palette.success.main,
                                                            color: theme.palette.success
                                                               .contrastText,
                                                            textTransform: 'none',
                                                            fontWeight: 500,
                                                            py: { xs: 1, sm: 0.75 },
                                                            fontSize: {
                                                               xs: '0.8rem',
                                                               sm: '0.875rem',
                                                            },
                                                            boxShadow: 'none',
                                                            '&:hover': {
                                                               backgroundColor:
                                                                  theme.palette.success.dark,
                                                               boxShadow: 'none',
                                                            },
                                                         }}
                                                      >
                                                         {approvingUsers.has(request.id)
                                                            ? 'Approving...'
                                                            : 'Approve Access'}
                                                      </Button>
                                                   </Box>
                                                )}

                                                {/* Status message for senders */}
                                                {isSender && (
                                                   <Box
                                                      sx={{
                                                         p: 2,
                                                         backgroundColor:
                                                            theme.palette.info.main + '20',
                                                         borderRadius: 1.5,
                                                         border: `1px solid ${theme.palette.info.main}40`,
                                                         textAlign: 'center',
                                                      }}
                                                   >
                                                      <Typography
                                                         variant='body2'
                                                         sx={{
                                                            color: theme.palette.info.main,
                                                            fontWeight: 500,
                                                            fontSize: '0.875rem',
                                                         }}
                                                      >
                                                         Waiting for album owner to approve your
                                                         request
                                                      </Typography>
                                                   </Box>
                                                )}
                                             </CardContent>
                                          </Card>
                                       );
                                    })}
                                 </Stack>
                              </Box>
                           )}

                           {/* Photographer profile dialog (US-05) */}
                           <Dialog
                              open={!!profilePopoverUserId}
                              onClose={() => {
                                 setProfilePopoverUserId(null);
                                 setProfilePopoverMessageMode(false);
                                 setProfilePopoverMessageText('');
                                 setProfilePopoverSenderEmail('');
                                 setProfilePopoverSenderName('');
                                 setProfilePopoverMessageError(null);
                              }}
                              maxWidth='sm'
                              fullWidth
                              PaperProps={{
                                 sx: {
                                    borderRadius: 2,
                                    overflow: 'hidden',
                                    background:
                                       'linear-gradient(165deg, #252530 0%, #1a1b22 50%, #16161a 100%)',
                                    border: '1px solid rgba(255,255,255,0.08)',
                                    boxShadow:
                                       '0 24px 48px rgba(0,0,0,0.32), 0 0 0 1px rgba(255,255,255,0.03) inset',
                                 },
                              }}
                           >
                              <DialogTitle
                                 sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'flex-end',
                                    py: 1.5,
                                    px: 2.5,
                                    borderBottom: '1px solid rgba(0,196,167,0.15)',
                                 }}
                              >
                                 <IconButton
                                    onClick={() => setProfilePopoverUserId(null)}
                                    sx={{
                                       color: 'rgba(0,196,167,0.8)',
                                       '&:hover': {
                                          color: '#00C4A7',
                                          bgcolor: 'rgba(0,196,167,0.1)',
                                       },
                                    }}
                                    size='small'
                                 >
                                    <CloseIcon fontSize='small' />
                                 </IconButton>
                              </DialogTitle>
                              <DialogContent
                                 sx={{
                                    pt: 2.5,
                                    pb: 2.5,
                                    px: 2.5,
                                    maxHeight: '75vh',
                                    overflow: 'hidden',
                                    display: 'flex',
                                    flexDirection: 'column',
                                 }}
                              >
                                 {profilePopoverLoading ? (
                                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                                       <CircularProgress sx={{ color: '#00C4A7' }} size={28} />
                                    </Box>
                                 ) : profilePopoverData?.photographerProfile ? (
                                    <Box
                                       sx={{
                                          display: 'flex',
                                          flexDirection: 'column',
                                          flex: 1,
                                          minHeight: 0,
                                       }}
                                    >
                                       {profilePopoverMessageMode ? (
                                          <>
                                             <Box
                                                sx={{
                                                   display: 'flex',
                                                   alignItems: 'center',
                                                   gap: 1,
                                                   mb: 2,
                                                }}
                                             >
                                                <Button
                                                   startIcon={<BackIconMsg />}
                                                   size='small'
                                                   onClick={() => {
                                                      setProfilePopoverMessageMode(false);
                                                      setProfilePopoverMessageError(null);
                                                   }}
                                                   sx={{ color: '#00C4A7', textTransform: 'none' }}
                                                >
                                                   Back
                                                </Button>
                                             </Box>
                                             <Typography
                                                variant='subtitle2'
                                                sx={{ color: 'rgba(255,255,255,0.8)', mb: 1.5 }}
                                             >
                                                Send a message to{' '}
                                                {profilePopoverData.photographerProfile.name ||
                                                   'the photographer'}
                                             </Typography>
                                             <TextField
                                                fullWidth
                                                multiline
                                                rows={4}
                                                placeholder='Your message...'
                                                value={profilePopoverMessageText}
                                                onChange={(e) =>
                                                   setProfilePopoverMessageText(e.target.value)
                                                }
                                                disabled={profilePopoverSending}
                                                size='small'
                                                sx={{
                                                   mb: 1.5,
                                                   '& .MuiOutlinedInput-root': {
                                                      bgcolor: 'rgba(255,255,255,0.06)',
                                                      color: '#fff',
                                                   },
                                                }}
                                             />
                                             {!currentUser && (
                                                <>
                                                   <TextField
                                                      fullWidth
                                                      required
                                                      type='email'
                                                      label='Your email (so they can reply)'
                                                      value={profilePopoverSenderEmail}
                                                      onChange={(e) =>
                                                         setProfilePopoverSenderEmail(
                                                            e.target.value,
                                                         )
                                                      }
                                                      disabled={profilePopoverSending}
                                                      size='small'
                                                      sx={{
                                                         mb: 1,
                                                         '& .MuiOutlinedInput-root': {
                                                            bgcolor: 'rgba(255,255,255,0.06)',
                                                            color: '#fff',
                                                         },
                                                      }}
                                                   />
                                                   <TextField
                                                      fullWidth
                                                      label='Your name (optional)'
                                                      value={profilePopoverSenderName}
                                                      onChange={(e) =>
                                                         setProfilePopoverSenderName(e.target.value)
                                                      }
                                                      disabled={profilePopoverSending}
                                                      size='small'
                                                      sx={{
                                                         mb: 1.5,
                                                         '& .MuiOutlinedInput-root': {
                                                            bgcolor: 'rgba(255,255,255,0.06)',
                                                            color: '#fff',
                                                         },
                                                      }}
                                                   />
                                                </>
                                             )}
                                             {profilePopoverMessageError && (
                                                <Alert
                                                   severity='error'
                                                   sx={{ mb: 1.5 }}
                                                   onClose={() =>
                                                      setProfilePopoverMessageError(null)
                                                   }
                                                >
                                                   {profilePopoverMessageError}
                                                </Alert>
                                             )}
                                             <Button
                                                variant='contained'
                                                fullWidth
                                                disabled={
                                                   profilePopoverSending ||
                                                   !profilePopoverMessageText.trim() ||
                                                   (!currentUser &&
                                                      !profilePopoverSenderEmail.trim())
                                                }
                                                startIcon={
                                                   profilePopoverSending ? (
                                                      <CircularProgress
                                                         size={18}
                                                         sx={{ color: 'inherit' }}
                                                      />
                                                   ) : (
                                                      <SendIcon />
                                                   )
                                                }
                                                onClick={async () => {
                                                   if (
                                                      !profilePopoverUserId ||
                                                      !profilePopoverMessageText.trim()
                                                   )
                                                      return;
                                                   if (
                                                      !currentUser &&
                                                      !profilePopoverSenderEmail.trim()
                                                   ) {
                                                      setProfilePopoverMessageError(
                                                         'Please enter your email so the photographer can reply.',
                                                      );
                                                      return;
                                                   }
                                                   setProfilePopoverMessageError(null);
                                                   setProfilePopoverSending(true);
                                                   try {
                                                      const headers: Record<string, string> = {
                                                         'Content-Type': 'application/json',
                                                      };
                                                      if (currentUser) {
                                                         const authHeaders = await getAuthHeaders();
                                                         Object.assign(headers, authHeaders);
                                                      }
                                                      const res = await fetch(
                                                         `${process.env.REACT_APP_API_URL}/api/profile/photographer-inquiry`,
                                                         {
                                                            method: 'POST',
                                                            headers,
                                                            body: JSON.stringify({
                                                               photographerUserId:
                                                                  profilePopoverUserId,
                                                               message:
                                                                  profilePopoverMessageText.trim(),
                                                               albumId: '',
                                                               albumTitle: undefined,
                                                               ...(profilePopoverSenderEmail.trim() && {
                                                                  senderEmail:
                                                                     profilePopoverSenderEmail.trim(),
                                                               }),
                                                               ...(profilePopoverSenderName.trim() && {
                                                                  senderName:
                                                                     profilePopoverSenderName.trim(),
                                                               }),
                                                            }),
                                                         },
                                                      );
                                                      const data = await res
                                                         .json()
                                                         .catch(() => ({}));
                                                      if (!res.ok) {
                                                         setProfilePopoverMessageError(
                                                            data.message ||
                                                               data.error ||
                                                               'Failed to send message',
                                                         );
                                                         return;
                                                      }
                                                      if (data.emailSent === false) {
                                                         setSnackbarMessage(
                                                            data.message ||
                                                               'Message saved, but the notification email to the photographer could not be sent. They can still see it in their Inbox.',
                                                         );
                                                         setSnackbarSeverity('warning');
                                                      } else {
                                                         setSnackbarMessage(
                                                            'Your message was sent. The photographer will see it in their Inbox.',
                                                         );
                                                         setSnackbarSeverity('success');
                                                      }
                                                      setSnackbarOpen(true);
                                                      setProfilePopoverMessageText('');
                                                      setProfilePopoverSenderEmail('');
                                                      setProfilePopoverSenderName('');
                                                      setProfilePopoverMessageMode(false);
                                                   } catch (err: any) {
                                                      setProfilePopoverMessageError(
                                                         err?.message ||
                                                            'Something went wrong. Please try again.',
                                                      );
                                                   } finally {
                                                      setProfilePopoverSending(false);
                                                   }
                                                }}
                                                sx={{
                                                   bgcolor: '#00C4A7',
                                                   color: '#fff',
                                                   '&:hover': { bgcolor: '#017A7F' },
                                                   textTransform: 'none',
                                                }}
                                             >
                                                {profilePopoverSending ? 'Sending…' : 'Send'}
                                             </Button>
                                          </>
                                       ) : (
                                          <>
                                             <Box
                                                sx={{
                                                   overflow: 'auto',
                                                   flex: 1,
                                                   minHeight: 0,
                                                   pr: 0.5,
                                                }}
                                             >
                                                <Box
                                                   sx={{
                                                      display: 'flex',
                                                      alignItems: 'center',
                                                      gap: 2,
                                                      mb: 2.5,
                                                      flexWrap: 'wrap',
                                                   }}
                                                >
                                                   <Box
                                                      sx={{ position: 'relative', flexShrink: 0 }}
                                                   >
                                                      <Avatar
                                                         src={
                                                            profilePopoverData.photographerProfile
                                                               .profilePhotoUrl
                                                         }
                                                         sx={{
                                                            width: 72,
                                                            height: 72,
                                                            border: '1px solid rgba(0,196,167,0.4)',
                                                            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                                                         }}
                                                      >
                                                         {profilePopoverData.photographerProfile.name?.charAt(
                                                            0,
                                                         ) || '?'}
                                                      </Avatar>
                                                      <Box
                                                         sx={{
                                                            position: 'absolute',
                                                            bottom: -2,
                                                            right: -2,
                                                         }}
                                                      >
                                                         <PhotographerBadge
                                                            variant='avatarOverlay'
                                                            size='md'
                                                         />
                                                      </Box>
                                                   </Box>
                                                   <Box>
                                                      <Typography
                                                         variant='subtitle1'
                                                         sx={{
                                                            color: '#00C4A7',
                                                            fontWeight: 500,
                                                            mb: 0.25,
                                                         }}
                                                      >
                                                         {profilePopoverData.photographerProfile
                                                            .name || 'Photographer'}
                                                      </Typography>
                                                      <Typography
                                                         variant='caption'
                                                         sx={{
                                                            color: 'rgba(0,196,167,0.9)',
                                                            display: 'block',
                                                         }}
                                                      >
                                                         Photographer
                                                      </Typography>
                                                      {profilePopoverData.photographerProfile
                                                         .photographerType && (
                                                         <Typography
                                                            variant='caption'
                                                            sx={{
                                                               color: 'rgba(255,255,255,0.6)',
                                                               display: 'block',
                                                               mt: 0.25,
                                                               textTransform: 'capitalize',
                                                            }}
                                                         >
                                                            {
                                                               profilePopoverData
                                                                  .photographerProfile
                                                                  .photographerType
                                                            }
                                                         </Typography>
                                                      )}
                                                      {profilePopoverData.photographerProfile
                                                         .portfolioPhotoCount != null && (
                                                         <Typography
                                                            variant='caption'
                                                            sx={{
                                                               color: 'rgba(255,255,255,0.6)',
                                                               display: 'flex',
                                                               alignItems: 'center',
                                                               gap: 0.5,
                                                               mt: 0.25,
                                                            }}
                                                         >
                                                            <PhotoLibraryStatIcon
                                                               sx={{
                                                                  fontSize: 12,
                                                                  color: 'rgba(255,255,255,0.7)',
                                                               }}
                                                            />
                                                            {
                                                               profilePopoverData
                                                                  .photographerProfile
                                                                  .portfolioPhotoCount
                                                            }{' '}
                                                            portfolio photos
                                                         </Typography>
                                                      )}
                                                   </Box>
                                                </Box>
                                                {profilePopoverData.photographerProfile
                                                   .companyPhotoUrl && (
                                                   <Box sx={{ mb: 2 }}>
                                                      <Typography
                                                         variant='caption'
                                                         sx={{
                                                            color: 'rgba(255,255,255,0.55)',
                                                            display: 'block',
                                                            mb: 0.75,
                                                         }}
                                                      >
                                                         Company / Studio
                                                      </Typography>
                                                      <Box
                                                         component='img'
                                                         src={
                                                            profilePopoverData.photographerProfile
                                                               .companyPhotoUrl
                                                         }
                                                         alt='Company'
                                                         sx={{
                                                            width: '100%',
                                                            maxHeight: 180,
                                                            objectFit: 'cover',
                                                            borderRadius: 1.5,
                                                            border:
                                                               '1px solid rgba(255,255,255,0.08)',
                                                         }}
                                                      />
                                                   </Box>
                                                )}
                                                {profilePopoverData.photographerProfile
                                                   .companyName && (
                                                   <Box
                                                      sx={{
                                                         display: 'flex',
                                                         alignItems: 'center',
                                                         gap: 1,
                                                         mb: 1,
                                                      }}
                                                   >
                                                      <BusinessIcon
                                                         sx={{ color: '#00C4A7', fontSize: 16 }}
                                                      />
                                                      <Typography
                                                         variant='body2'
                                                         sx={{
                                                            color: 'rgba(255,255,255,0.9)',
                                                            fontWeight: 400,
                                                         }}
                                                      >
                                                         {
                                                            profilePopoverData.photographerProfile
                                                               .companyName
                                                         }
                                                      </Typography>
                                                   </Box>
                                                )}
                                                {profilePopoverData.photographerProfile
                                                   .description && (
                                                   <Box sx={{ mb: 2 }}>
                                                      <Typography
                                                         variant='body2'
                                                         sx={{
                                                            color: 'rgba(255,255,255,0.75)',
                                                            lineHeight: 1.55,
                                                            fontWeight: 400,
                                                         }}
                                                      >
                                                         {
                                                            profilePopoverData.photographerProfile
                                                               .description
                                                         }
                                                      </Typography>
                                                   </Box>
                                                )}
                                                <Box
                                                   sx={{
                                                      display: 'flex',
                                                      flexDirection: 'column',
                                                      gap: 1,
                                                   }}
                                                >
                                                   {profilePopoverData.photographerProfile
                                                      .contactNumber && (
                                                      <Box
                                                         sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 1,
                                                         }}
                                                      >
                                                         <PhoneIcon
                                                            sx={{ color: '#00C4A7', fontSize: 16 }}
                                                         />
                                                         <Typography
                                                            component='a'
                                                            href={`tel:${profilePopoverData.photographerProfile.contactNumber}`}
                                                            variant='body2'
                                                            sx={{
                                                               color: '#00C4A7',
                                                               textDecoration: 'none',
                                                               fontWeight: 400,
                                                               '&:hover': {
                                                                  color: 'rgba(0,196,167,0.85)',
                                                               },
                                                            }}
                                                         >
                                                            {
                                                               profilePopoverData
                                                                  .photographerProfile.contactNumber
                                                            }
                                                         </Typography>
                                                      </Box>
                                                   )}
                                                   {profilePopoverData.photographerProfile
                                                      .website && (
                                                      <Box
                                                         sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 1,
                                                         }}
                                                      >
                                                         <LanguageIcon
                                                            sx={{ color: '#00C4A7', fontSize: 16 }}
                                                         />
                                                         <Typography
                                                            component='a'
                                                            href={
                                                               profilePopoverData
                                                                  .photographerProfile.website
                                                            }
                                                            target='_blank'
                                                            rel='noopener noreferrer'
                                                            variant='body2'
                                                            sx={{
                                                               color: '#00C4A7',
                                                               fontWeight: 400,
                                                               '&:hover': {
                                                                  color: 'rgba(0,196,167,0.85)',
                                                               },
                                                            }}
                                                         >
                                                            {
                                                               profilePopoverData
                                                                  .photographerProfile.website
                                                            }
                                                         </Typography>
                                                      </Box>
                                                   )}
                                                </Box>
                                             </Box>
                                             <Box
                                                sx={{
                                                   flexShrink: 0,
                                                   borderTop: '1px solid rgba(255,255,255,0.08)',
                                                   pt: 2,
                                                   mt: 2,
                                                }}
                                             >
                                                <Button
                                                   variant='outlined'
                                                   fullWidth
                                                   startIcon={<MessageIcon />}
                                                   onClick={() =>
                                                      setProfilePopoverMessageMode(true)
                                                   }
                                                   sx={{
                                                      borderColor: '#00C4A7',
                                                      color: '#00C4A7',
                                                      textTransform: 'none',
                                                      '&:hover': {
                                                         borderColor: '#017A7F',
                                                         bgcolor: 'rgba(0,196,167,0.08)',
                                                      },
                                                   }}
                                                >
                                                   Message
                                                </Button>
                                             </Box>
                                          </>
                                       )}
                                    </Box>
                                 ) : profilePopoverUserId && !profilePopoverLoading ? (
                                    <Box sx={{ py: 2.5, textAlign: 'center' }}>
                                       <Typography
                                          variant='body2'
                                          sx={{
                                             color: 'rgba(255,255,255,0.6)',
                                             fontWeight: 400,
                                          }}
                                       >
                                          Photographer profile not available for view.
                                       </Typography>
                                    </Box>
                                 ) : null}
                              </DialogContent>
                           </Dialog>

                           {/* Photographer lightbox */}
                           <Dialog
                              open={lightboxOpen}
                              onClose={() => setLightboxOpen(false)}
                              maxWidth='md'
                              fullWidth
                           >
                              <DialogContent
                                 sx={{
                                    bgcolor: 'black',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    position: 'relative',
                                    p: 0,
                                 }}
                              >
                                 {lightboxUrls[lightboxIndex] && (
                                    <Box
                                       component='img'
                                       src={lightboxUrls[lightboxIndex]}
                                       alt='Photo'
                                       sx={{
                                          width: '100%',
                                          maxHeight: '80vh',
                                          objectFit: 'contain',
                                       }}
                                    />
                                 )}
                                 <IconButton
                                    onClick={() => setLightboxOpen(false)}
                                    sx={{
                                       position: 'absolute',
                                       top: 8,
                                       right: 8,
                                       color: 'white',
                                       bgcolor: 'rgba(0,0,0,0.4)',
                                       '&:hover': { bgcolor: 'rgba(0,0,0,0.6)' },
                                    }}
                                 >
                                    <CloseIcon />
                                 </IconButton>
                                 {lightboxUrls.length > 1 && (
                                    <>
                                       <IconButton
                                          onClick={() =>
                                             setLightboxIndex((prev) =>
                                                prev === 0 ? lightboxUrls.length - 1 : prev - 1,
                                             )
                                          }
                                          sx={{
                                             position: 'absolute',
                                             left: 8,
                                             top: '50%',
                                             transform: 'translateY(-50%)',
                                             color: 'white',
                                             bgcolor: 'rgba(0,0,0,0.4)',
                                             '&:hover': { bgcolor: 'rgba(0,0,0,0.6)' },
                                          }}
                                       >
                                          ‹
                                       </IconButton>
                                       <IconButton
                                          onClick={() =>
                                             setLightboxIndex((prev) =>
                                                prev === lightboxUrls.length - 1 ? 0 : prev + 1,
                                             )
                                          }
                                          sx={{
                                             position: 'absolute',
                                             right: 8,
                                             top: '50%',
                                             transform: 'translateY(-50%)',
                                             color: 'white',
                                             bgcolor: 'rgba(0,0,0,0.4)',
                                             '&:hover': { bgcolor: 'rgba(0,0,0,0.6)' },
                                          }}
                                       >
                                          ›
                                       </IconButton>
                                    </>
                                 )}
                              </DialogContent>
                           </Dialog>
                           {/* Invitations are now shown in the sorted feed cards above */}

                           {/* Profile Update Card - Show at top if profile is incomplete */}
                           {activeFilter === 'all' &&
                              !loadingProfileStatus &&
                              profileStatus &&
                              profileStatus.completionPercentage < 100 &&
                              currentUser && (
                                 <Box sx={{ mb: 1.5 }}>
                                    <ProfileUpdateCard
                                       profileStatus={profileStatus}
                                       userId={currentUser.uid}
                                       onProfileRefresh={async () => {
                                          // Re-check profile completion status without opening the dialog
                                          try {
                                             const status = await checkProfileCompletion(
                                                currentUser.uid,
                                                currentUser.displayName,
                                                currentUser.photoURL,
                                             );
                                             setProfileStatus(status);
                                          } catch (error) {
                                             console.error(
                                                'Error re-checking profile completion:',
                                                error,
                                             );
                                          }
                                       }}
                                    />
                                 </Box>
                              )}

                           {/* Feed Filter Bar — fixed at bottom of viewport */}
                           <Box
                              sx={{
                                 position: 'fixed',
                                 bottom: 0,
                                 left: { xs: 0, sm: '270px' },
                                 right: { xs: 0, lg: '440px' },
                                 zIndex: 1200,
                                 background: 'rgba(8,10,15,0.92)',
                                 backdropFilter: 'blur(12px)',
                                 WebkitBackdropFilter: 'blur(12px)',
                                 boxShadow: '0 -4px 20px rgba(0,0,0,0.3)',
                                 px: { xs: 1.5, sm: 2 },
                                 py: { xs: 1, sm: 1.5 },
                                 borderTop: '1px solid rgba(255,255,255,0.08)',
                                 display: 'flex',
                                 gap: 1,
                                 justifyContent: 'center',
                                 overflowX: 'auto',
                                 whiteSpace: 'nowrap',
                                 '&::-webkit-scrollbar': { display: 'none' },
                                 scrollbarWidth: 'none',
                                 transform: filterBarVisible ? 'translateY(0)' : 'translateY(100%)',
                                 transition: 'transform 0.25s ease',
                              }}
                           >
                              {[
                                 { key: 'all' as const, label: 'All', activeColor: '#1D9E75' },
                                 { key: 'photos' as const, label: 'My Photos', activeColor: '#1D9E75' },
                                 { key: 'hot' as const, label: 'Hot', activeColor: '#C0392B' },
                                 { key: 'analytics' as const, label: 'Analytics', activeColor: '#1D9E75' },
                                 { key: 'notifications' as const, label: 'Notifications', activeColor: '#1D9E75' },
                              ].map(({ key, label, activeColor }) => {
                                 const count = filterCounts[key];
                                 const isActive = activeFilter === key;
                                 const isHot = key === 'hot';
                                 return (
                                    <Box
                                       key={key}
                                       component='button'
                                       onClick={() => setActiveFilter(key)}
                                       sx={{
                                          background: isActive
                                             ? activeColor
                                             : 'rgba(255,255,255,0.07)',
                                          border: isActive
                                             ? `1px solid ${activeColor}`
                                             : '1px solid rgba(255,255,255,0.12)',
                                          color: isActive ? '#fff' : 'rgba(255,255,255,0.6)',
                                          borderRadius: '20px',
                                          px: { xs: 2.5, sm: 2 },
                                          py: { xs: 1, sm: 0.75 },
                                          fontSize: { xs: '14px', sm: '13px' },
                                          fontWeight: 500,
                                          cursor: 'pointer',
                                          flexShrink: 0,
                                          fontFamily: 'inherit',
                                          transition: 'all 0.15s ease',
                                          display: 'inline-flex',
                                          alignItems: 'center',
                                          gap: 0.5,
                                          '&:hover': {
                                             background: isActive
                                                ? activeColor
                                                : 'rgba(255,255,255,0.12)',
                                          },
                                          '@keyframes hotPulse': {
                                             '0%, 100%': { transform: 'scale(1)', opacity: 0.85 },
                                             '50%': { transform: 'scale(1.18)', opacity: 1 },
                                          },
                                       }}
                                    >
                                       {isHot && (
                                          <Box
                                             component='span'
                                             sx={{
                                                display: 'inline-block',
                                                fontSize: { xs: '15px', sm: '14px' },
                                                lineHeight: 1,
                                                animation: !isActive ? 'hotPulse 2s ease-in-out infinite' : 'none',
                                             }}
                                          >
                                             {'\u{1F525}'}
                                          </Box>
                                       )}
                                       {label}
                                       {count > 0 && (
                                          <Box
                                             component='span'
                                             sx={{
                                                fontSize: '10px',
                                                fontWeight: 700,
                                                background: isActive
                                                   ? 'rgba(255,255,255,0.25)'
                                                   : 'rgba(255,255,255,0.15)',
                                                borderRadius: '8px',
                                                px: 0.75,
                                                py: 0.125,
                                                lineHeight: 1.4,
                                             }}
                                          >
                                             {count}
                                          </Box>
                                       )}
                                    </Box>
                                 );
                              })}
                           </Box>

                           {/* Loading State */}
                           {(loadingLinkClicks || loadingDownloads) && (
                              <Box
                                 sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    py: 3,
                                 }}
                              >
                                 <CircularProgress size={24} sx={{ color: '#3b82f6' }} />
                                 <Typography variant='body2' sx={{ ml: 2, color: '#6b7280' }}>
                                    Loading feed cards...
                                 </Typography>
                              </Box>
                           )}

                           {/* All Feed Cards - Sorted by Date */}
                           {!loadingLinkClicks &&
                              !loadingDownloads &&
                              !loadingReceivedInvitations &&
                              !loadingSentInvitations &&
                              !loading &&
                              sortedFeedCards.length > 0 && (
                                 <>
                                    <Stack spacing={1.5}>
                                    {visibleFeedCards.map((card) => {
                                       switch (card.type) {
                                          case 'linkClick':
                                             return (
                                                <AlbumLinkClickCard
                                                   key={card.id}
                                                   linkClick={card.data as AlbumLinkClick}
                                                   onViewAlbum={handleViewAlbum}
                                                />
                                             );
                                          case 'download':
                                             return (
                                                <AlbumDownloadCard
                                                   key={card.id}
                                                   download={card.data as AlbumDownload}
                                                   onViewAlbum={handleViewAlbum}
                                                />
                                             );
                                          case 'albumCreated': {
                                             const createdData = card.data as AlbumCreated;
                                             return (
                                                <React.Fragment key={card.id}>
                                                   <input
                                                      type='file'
                                                      accept='image/*'
                                                      multiple
                                                      hidden
                                                      ref={(el) => {
                                                         fileInputRefs.current[
                                                            createdData.albumId
                                                         ] = el;
                                                      }}
                                                      onChange={(e) =>
                                                         handleFeedUpload(
                                                            createdData.albumId,
                                                            e.target.files,
                                                         )
                                                      }
                                                   />
                                                   <AlbumCreatedCard
                                                      albumCreated={createdData}
                                                      onViewAlbum={handleViewAlbum}
                                                      onUpload={(albumId) =>
                                                         fileInputRefs.current[albumId]?.click()
                                                      }
                                                      isUploading={
                                                         uploadingAlbumId === createdData.albumId
                                                      }
                                                   />
                                                </React.Fragment>
                                             );
                                          }
                                          case 'eventCreated':
                                             return (
                                                <EventCreatedCard
                                                   key={card.id}
                                                   eventCreated={card.data as EventCreated}
                                                   onViewEvent={(eventId) =>
                                                      navigate(`/events/${eventId}`)
                                                   }
                                                />
                                             );
                                          case 'invitationReceived':
                                             return (
                                                <AlbumInvitationReceivedCard
                                                   key={card.id}
                                                   invitation={card.data as AlbumInvitationReceived}
                                                   onAcceptSuccess={handleAcceptSuccess}
                                                   onDeclineSuccess={handleDeclineSuccess}
                                                   onViewAlbum={handleViewAlbum}
                                                />
                                             );
                                          case 'invitationSent':
                                             return (
                                                <AlbumInvitationSentCard
                                                   key={card.id}
                                                   invitation={card.data as AlbumInvitationSent}
                                                   onResendSuccess={handleResendSuccess}
                                                   onCancelSuccess={handleCancelSuccess}
                                                   onViewAlbum={handleViewAlbum}
                                                />
                                             );
                                          case 'accessRequest':
                                             const request = card.data as AlbumAccessRequest;
                                             const isOwner =
                                                request.albumOwnerId === currentUser?.uid;
                                             const isSender =
                                                request.requesterId === currentUser?.uid;
                                             return (
                                                <Card
                                                   key={card.id}
                                                   sx={{
                                                      borderRadius: 2,
                                                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                                                      border: `1px solid ${theme.palette.divider}`,
                                                      borderLeft: `3px solid ${theme.palette.warning.main}`,
                                                      overflow: 'visible',
                                                      background: theme.palette.background.paper,
                                                      transition: 'all 0.2s ease-in-out',
                                                      '&:hover': {
                                                         boxShadow: isOwner
                                                            ? '0 4px 16px rgba(79, 70, 229, 0.15)'
                                                            : '0 4px 16px rgba(8, 145, 178, 0.15)',
                                                         transform: 'translateY(-1px)',
                                                      },
                                                   }}
                                                >
                                                   <CardContent sx={{ p: 2.5 }}>
                                                      <Box
                                                         sx={{
                                                            display: 'flex',
                                                            alignItems: 'flex-start',
                                                            gap: 2,
                                                         }}
                                                      >
                                                         <Avatar
                                                            src={request.requesterPhoto}
                                                            sx={{
                                                               width: 48,
                                                               height: 48,
                                                               border: `2px solid ${theme.palette.background.paper}`,
                                                               boxShadow: theme.shadows[1],
                                                            }}
                                                         >
                                                            {request.requesterName?.[0] || '?'}
                                                         </Avatar>
                                                         <Box sx={{ flex: 1 }}>
                                                            <Typography
                                                               variant='subtitle1'
                                                               sx={{ fontWeight: 600, mb: 0.5 }}
                                                            >
                                                               {isOwner
                                                                  ? `${request.requesterName} requested access`
                                                                  : 'Access request sent'}
                                                            </Typography>
                                                            <Typography
                                                               variant='body2'
                                                               color='text.secondary'
                                                            >
                                                               Album: {request.albumName}
                                                            </Typography>
                                                            {isOwner && (
                                                               <Box
                                                                  sx={{
                                                                     mt: 2,
                                                                     display: 'flex',
                                                                     gap: 1,
                                                                  }}
                                                               >
                                                                  <Button
                                                                     variant='contained'
                                                                     size='small'
                                                                     onClick={() =>
                                                                        handleApprove(
                                                                           request.id,
                                                                           request.albumId,
                                                                           request.requesterId,
                                                                        )
                                                                     }
                                                                     disabled={approvingUsers.has(
                                                                        request.id,
                                                                     )}
                                                                     sx={{
                                                                        backgroundColor:
                                                                           theme.palette.success
                                                                              .main,
                                                                        '&:hover': {
                                                                           backgroundColor:
                                                                              theme.palette.success
                                                                                 .dark,
                                                                        },
                                                                     }}
                                                                  >
                                                                     {approvingUsers.has(request.id)
                                                                        ? 'Approving...'
                                                                        : 'Approve Access'}
                                                                  </Button>
                                                               </Box>
                                                            )}
                                                            {isSender && (
                                                               <Box
                                                                  sx={{
                                                                     mt: 2,
                                                                     p: 2,
                                                                     backgroundColor:
                                                                        theme.palette.info.main +
                                                                        '20',
                                                                     borderRadius: 1.5,
                                                                     border: `1px solid ${theme.palette.info.main}40`,
                                                                  }}
                                                               >
                                                                  <Typography
                                                                     variant='body2'
                                                                     sx={{
                                                                        color: theme.palette.info
                                                                           .main,
                                                                        fontWeight: 500,
                                                                     }}
                                                                  >
                                                                     Waiting for album owner to
                                                                     approve your request
                                                                  </Typography>
                                                               </Box>
                                                            )}
                                                         </Box>
                                                      </Box>
                                                   </CardContent>
                                                </Card>
                                             );
                                          case 'sponsor':
                                             return (
                                                <SponsorCard
                                                   key={card.id}
                                                   sponsor={card.data as SponsorCardData}
                                                   userId={currentUser?.uid || ''}
                                                />
                                             );
                                          default:
                                             return null;
                                       }
                                    })}
                                 </Stack>
                                    {visibleFeedCardCount < sortedFeedCards.length && (
                                       <Box
                                          ref={feedLoadMoreSentinelRef}
                                          sx={{ height: 24, flexShrink: 0 }}
                                          aria-hidden
                                       />
                                    )}
                                 </>
                              )}

                           {/* Albums Refresh Indicator - Shows when albums are being refreshed */}
                           {loadingAlbums && !loading && albumsRefreshTrigger > 0 && (
                              <Box
                                 sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: 1.5,
                                    py: 1.5,
                                    px: 2,
                                    backgroundColor: theme.palette.info.main + '20',
                                    borderRadius: 1.5,
                                    border: `1px solid ${theme.palette.info.main}40`,
                                 }}
                              >
                                 <CircularProgress
                                    size={18}
                                    sx={{ color: theme.palette.info.main }}
                                 />
                                 <Typography
                                    variant='body2'
                                    sx={{ color: theme.palette.info.main, fontWeight: 500 }}
                                 >
                                    Refreshing albums...
                                 </Typography>
                              </Box>
                           )}

                           {/* Filter Empty State */}
                           {activeFilter !== 'all' &&
                              !loading &&
                              !loadingLinkClicks &&
                              !loadingDownloads &&
                              !loadingReceivedInvitations &&
                              !loadingSentInvitations &&
                              sortedFeedCards.length === 0 &&
                              !(activeFilter === 'photos' && accessibleAlbums.length > 0) && (
                                 <Paper
                                    sx={{
                                       p: 5,
                                       textAlign: 'center',
                                       backgroundColor: 'rgba(255,255,255,0.03)',
                                       border: '1px solid rgba(255,255,255,0.08)',
                                       borderRadius: 2,
                                       boxShadow: 'none',
                                    }}
                                 >
                                    <Typography sx={{ fontSize: 48, mb: 1 }}>
                                       {activeFilter === 'photos'
                                          ? '\u{1F4F7}'
                                          : activeFilter === 'analytics'
                                            ? '\u{1F4CA}'
                                            : activeFilter === 'hot'
                                              ? '\u{1F525}'
                                              : '\u{1F514}'}
                                    </Typography>
                                    <Typography
                                       variant='h6'
                                       sx={{
                                          color: 'rgba(255,255,255,0.85)',
                                          fontWeight: 600,
                                          mb: 0.5,
                                       }}
                                    >
                                       {activeFilter === 'hot'
                                          ? 'Nothing hot right now'
                                          : `No ${
                                               activeFilter === 'photos'
                                                  ? 'photos'
                                                  : activeFilter === 'analytics'
                                                    ? 'analytics'
                                                    : 'notifications'
                                            } to show yet`}
                                    </Typography>
                                    <Typography
                                       variant='body2'
                                       sx={{ color: 'rgba(255,255,255,0.45)', mb: 2.5 }}
                                    >
                                       Check back after your next event
                                    </Typography>
                                    <Button
                                       variant='outlined'
                                       onClick={() => setActiveFilter('all')}
                                       sx={{
                                          borderColor: 'rgba(255,255,255,0.2)',
                                          color: 'rgba(255,255,255,0.7)',
                                          textTransform: 'none',
                                          borderRadius: '20px',
                                          px: 3,
                                          '&:hover': {
                                             borderColor: 'rgba(255,255,255,0.4)',
                                             backgroundColor: 'rgba(255,255,255,0.05)',
                                          },
                                       }}
                                    >
                                       Show All
                                    </Button>
                                 </Paper>
                              )}

                           {/* Accessible Albums Section */}
                           {(activeFilter === 'all' || activeFilter === 'photos') &&
                              !loadingAlbums &&
                              accessibleAlbums.length > 0 && (
                                 <Box>
                                    <Stack spacing={1.5}>
                                       {accessibleAlbums.map((album) => (
                                          <Card
                                             key={album.id}
                                             sx={{
                                                borderRadius: 1.5,
                                                boxShadow: theme.shadows[0],
                                                border: `1px solid ${theme.palette.divider}`,
                                                borderLeft:
                                                   album.type === 'private'
                                                      ? `3px solid ${theme.palette.secondary.main}`
                                                      : `3px solid ${theme.palette.success.main}`,
                                                overflow: 'visible',
                                                backgroundColor: theme.palette.background.paper,
                                             }}
                                          >
                                             <CardContent
                                                sx={{
                                                   p: { xs: 1.25, sm: 1.75 },
                                                   '&:last-child': { pb: { xs: 1.25, sm: 1.75 } },
                                                }}
                                             >
                                                {/* Header */}
                                                <Box
                                                   sx={{
                                                      display: 'flex',
                                                      flexDirection: { xs: 'column', sm: 'row' },
                                                      justifyContent: 'space-between',
                                                      alignItems: {
                                                         xs: 'flex-start',
                                                         sm: 'flex-start',
                                                      },
                                                      gap: { xs: 1, sm: 0 },
                                                      mb: 1.5,
                                                   }}
                                                >
                                                   <Box
                                                      sx={{
                                                         display: 'flex',
                                                         gap: { xs: 1, sm: 1.25 },
                                                         alignItems: 'center',
                                                      }}
                                                   >
                                                      <Box
                                                         sx={{
                                                            width: 36,
                                                            height: 36,
                                                            borderRadius: 1.25,
                                                            overflow: 'hidden',
                                                            backgroundColor: album.coverPhotoUrl
                                                               ? 'transparent'
                                                               : '#f3e8ff',
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            border: album.coverPhotoUrl
                                                               ? '2px solid #8b5cf6'
                                                               : 'none',
                                                            flexShrink: 0,
                                                         }}
                                                      >
                                                         {album.coverPhotoUrl ? (
                                                            <img
                                                               src={album.coverPhotoUrl}
                                                               alt={album.title}
                                                               style={{
                                                                  width: '100%',
                                                                  height: '100%',
                                                                  objectFit: 'cover',
                                                               }}
                                                            />
                                                         ) : (
                                                            <PhotoLibraryIcon
                                                               sx={{
                                                                  color: '#8b5cf6',
                                                                  fontSize: 18,
                                                               }}
                                                            />
                                                         )}
                                                      </Box>
                                                      <Box sx={{ minWidth: 0 }}>
                                                         <Typography
                                                            variant='subtitle1'
                                                            sx={{
                                                               fontWeight: 600,
                                                               color: theme.palette.text.primary,
                                                               fontSize: '0.875rem',
                                                               lineHeight: 1.3,
                                                            }}
                                                         >
                                                            {album.title}
                                                         </Typography>
                                                         <Typography
                                                            variant='body2'
                                                            color='text.secondary'
                                                            sx={{
                                                               fontSize: '0.7rem',
                                                               lineHeight: 1.2,
                                                            }}
                                                         >
                                                            You have access to this album
                                                         </Typography>
                                                      </Box>
                                                   </Box>
                                                   <Chip
                                                      label={
                                                         album.type === 'private'
                                                            ? 'Private'
                                                            : album.type === 'public'
                                                              ? 'Public'
                                                              : 'Paid'
                                                      }
                                                      size='small'
                                                      icon={
                                                         album.type === 'private' ? (
                                                            <LockIcon sx={{ fontSize: 12 }} />
                                                         ) : album.type === 'public' ? (
                                                            <PublicIcon sx={{ fontSize: 12 }} />
                                                         ) : undefined
                                                      }
                                                      sx={{
                                                         backgroundColor:
                                                            album.type === 'private'
                                                               ? '#8b5cf6'
                                                               : album.type === 'public'
                                                                 ? '#10b981'
                                                                 : '#f59e0b',
                                                         color: '#ffffff',
                                                         fontWeight: 500,
                                                         height: 22,
                                                         fontSize: '0.7rem',
                                                         '& .MuiChip-icon': {
                                                            color: '#ffffff',
                                                         },
                                                      }}
                                                   />
                                                </Box>

                                                {/* Owner Info */}
                                                <Box
                                                   sx={{
                                                      display: 'flex',
                                                      alignItems: 'center',
                                                      justifyContent: 'space-between',
                                                      mb: 1.5,
                                                   }}
                                                >
                                                   <Box
                                                      sx={{
                                                         display: 'flex',
                                                         alignItems: 'center',
                                                         gap: 1.25,
                                                      }}
                                                   >
                                                      <Avatar
                                                         src={album.ownerPhoto || ''}
                                                         alt={album.ownerName || 'Owner'}
                                                         sx={{
                                                            width: 28,
                                                            height: 28,
                                                            fontSize: '0.875rem',
                                                         }}
                                                      >
                                                         {(album.ownerName || 'O').charAt(0)}
                                                      </Avatar>
                                                      <Box>
                                                         <Typography
                                                            variant='body2'
                                                            sx={{
                                                               fontWeight: 500,
                                                               fontSize: '0.8rem',
                                                               lineHeight: 1.3,
                                                            }}
                                                         >
                                                            By {album.ownerName || 'Unknown'}
                                                         </Typography>
                                                         <Typography
                                                            variant='caption'
                                                            color='text.secondary'
                                                            sx={{
                                                               fontSize: '0.7rem',
                                                               lineHeight: 1.2,
                                                            }}
                                                         >
                                                            {album.photoCount} photos
                                                            {album.groupCount &&
                                                               album.groupCount > 0 &&
                                                               ` • ${album.groupCount} groups`}
                                                         </Typography>
                                                      </Box>
                                                   </Box>
                                                   {album.accessGrantedAt && (
                                                      <Typography
                                                         variant='caption'
                                                         color='text.secondary'
                                                         sx={{
                                                            fontSize: '0.7rem',
                                                            textAlign: 'right',
                                                            lineHeight: 1.3,
                                                         }}
                                                      >
                                                         Access granted
                                                         <br />
                                                         {getTimeAgo(album.accessGrantedAt)}
                                                      </Typography>
                                                   )}
                                                </Box>

                                                {/* Event Association */}
                                                {album.eventTitle && (
                                                   <Box
                                                      sx={{
                                                         display: 'flex',
                                                         alignItems: 'center',
                                                         gap: 0.75,
                                                         mb: 1.5,
                                                      }}
                                                   >
                                                      <EventIcon
                                                         sx={{ fontSize: 14, color: '#64748b' }}
                                                      />
                                                      <Typography
                                                         variant='body2'
                                                         sx={{
                                                            fontSize: '0.8rem',
                                                            color: '#475569',
                                                            lineHeight: 1.3,
                                                         }}
                                                      >
                                                         {album.eventTitle}
                                                         {album.eventDate && (
                                                            <span style={{ color: '#94a3b8' }}>
                                                               {' '}
                                                               •{' '}
                                                               {new Date(
                                                                  album.eventDate.seconds * 1000,
                                                               ).toLocaleDateString('en-US', {
                                                                  month: 'short',
                                                                  day: 'numeric',
                                                                  year: 'numeric',
                                                               })}
                                                            </span>
                                                         )}
                                                      </Typography>
                                                   </Box>
                                                )}

                                                <InlinePhotoScroller
                                                   albumId={album.id}
                                                   albumTitle={album.title || 'Album'}
                                                   initialPhotoCount={album.photoCount}
                                                />

                                                {/* Actions */}
                                                <Box
                                                   sx={{
                                                      display: 'flex',
                                                      flexDirection: { xs: 'column', sm: 'row' },
                                                      gap: { xs: 1, sm: 1.25 },
                                                   }}
                                                >
                                                   <Button
                                                      variant='contained'
                                                      fullWidth
                                                      endIcon={
                                                         <OpenInNewIcon
                                                            sx={{ fontSize: { xs: 14, sm: 16 } }}
                                                         />
                                                      }
                                                      onClick={() => handleViewAlbum(album.id)}
                                                      sx={{
                                                         backgroundColor: theme.palette.info.main,
                                                         color: theme.palette.info.contrastText,
                                                         textTransform: 'none',
                                                         fontWeight: 500,
                                                         py: { xs: 0.8, sm: 0.6 },
                                                         fontSize: { xs: '0.75rem', sm: '0.8rem' },
                                                         boxShadow: 'none',
                                                         '&:hover': {
                                                            backgroundColor:
                                                               theme.palette.info.dark,
                                                            boxShadow: 'none',
                                                         },
                                                      }}
                                                   >
                                                      View All Photos
                                                   </Button>

                                                   {(album.feedMemberRole === 'owner' ||
                                                      album.feedMemberRole === 'editor' ||
                                                      album.enableParticipantUpload ||
                                                      album.allowPhotographerUpload) && (
                                                      <>
                                                         <input
                                                            type='file'
                                                            accept='image/*'
                                                            multiple
                                                            hidden
                                                            ref={(el) => {
                                                               fileInputRefs.current[album.id] = el;
                                                            }}
                                                            onChange={(e) =>
                                                               handleFeedUpload(
                                                                  album.id,
                                                                  e.target.files,
                                                               )
                                                            }
                                                         />
                                                         <Button
                                                            variant='outlined'
                                                            fullWidth
                                                            disabled={uploadingAlbumId === album.id}
                                                            startIcon={
                                                               uploadingAlbumId === album.id ? (
                                                                  <CircularProgress
                                                                     size={14}
                                                                     color='inherit'
                                                                  />
                                                               ) : (
                                                                  <CloudUploadIcon
                                                                     sx={{
                                                                        fontSize: {
                                                                           xs: 14,
                                                                           sm: 16,
                                                                        },
                                                                     }}
                                                                  />
                                                               )
                                                            }
                                                            onClick={() =>
                                                               fileInputRefs.current[
                                                                  album.id
                                                               ]?.click()
                                                            }
                                                            sx={{
                                                               borderColor:
                                                                  'rgba(255,255,255,0.25)',
                                                               color: 'rgba(255,255,255,0.85)',
                                                               textTransform: 'none',
                                                               fontWeight: 500,
                                                               py: { xs: 0.8, sm: 0.6 },
                                                               fontSize: {
                                                                  xs: '0.75rem',
                                                                  sm: '0.8rem',
                                                               },
                                                               '&:hover': {
                                                                  borderColor:
                                                                     'rgba(255,255,255,0.5)',
                                                                  backgroundColor:
                                                                     'rgba(255,255,255,0.06)',
                                                               },
                                                            }}
                                                         >
                                                            {uploadingAlbumId === album.id
                                                               ? 'Uploading…'
                                                               : 'Upload'}
                                                         </Button>
                                                      </>
                                                   )}

                                                   <Button
                                                      variant='outlined'
                                                      fullWidth
                                                      startIcon={
                                                         <OpenInNewIcon
                                                            sx={{ fontSize: { xs: 14, sm: 16 } }}
                                                         />
                                                      }
                                                      onClick={() =>
                                                         window.open(
                                                            `/albums/public/${album.id}`,
                                                            '_blank',
                                                         )
                                                      }
                                                      sx={{
                                                         borderColor: 'rgba(46,196,182,0.4)',
                                                         color: '#2EC4B6',
                                                         textTransform: 'none',
                                                         fontWeight: 500,
                                                         py: { xs: 0.8, sm: 0.6 },
                                                         fontSize: { xs: '0.75rem', sm: '0.8rem' },
                                                         '&:hover': {
                                                            borderColor: 'rgba(46,196,182,0.6)',
                                                            backgroundColor:
                                                               'rgba(46,196,182,0.06)',
                                                         },
                                                      }}
                                                   >
                                                      View Gallery
                                                   </Button>
                                                </Box>
                                             </CardContent>
                                          </Card>
                                       ))}
                                    </Stack>

                                    {/* Sentinel for infinite scroll + loading skeletons */}
                                    <div ref={albumSentinelRef} style={{ height: 1 }} />
                                    {loadingMoreAlbums && (
                                       <Stack spacing={1.5} sx={{ mt: 1.5 }}>
                                          {[0, 1].map((i) => (
                                             <Card
                                                key={`skeleton-album-${i}`}
                                                sx={{
                                                   borderRadius: 1.5,
                                                   border: `1px solid ${theme.palette.divider}`,
                                                   overflow: 'hidden',
                                                }}
                                             >
                                                <CardContent
                                                   sx={{ p: 1.75, '&:last-child': { pb: 1.75 } }}
                                                >
                                                   <Box
                                                      sx={{
                                                         display: 'flex',
                                                         gap: 1.25,
                                                         alignItems: 'center',
                                                      }}
                                                   >
                                                      <Skeleton
                                                         variant='rounded'
                                                         width={36}
                                                         height={36}
                                                      />
                                                      <Box sx={{ flex: 1 }}>
                                                         <Skeleton
                                                            variant='text'
                                                            width='60%'
                                                            height={20}
                                                         />
                                                         <Skeleton
                                                            variant='text'
                                                            width='40%'
                                                            height={14}
                                                         />
                                                      </Box>
                                                   </Box>
                                                </CardContent>
                                             </Card>
                                          ))}
                                       </Stack>
                                    )}
                                 </Box>
                              )}

                           {/* Empty State - Only show if no feed content at all */}
                           {accessRequests.length === 0 &&
                              accessibleAlbums.length === 0 &&
                              receivedInvitations.length === 0 &&
                              sentInvitations.length === 0 &&
                              albumLinkClicks.length === 0 &&
                              albumDownloads.length === 0 &&
                              mergedAlbumFeedCards.length === 0 &&
                              eventsCreated.length === 0 &&
                              !loading &&
                              !loadingAlbums &&
                              !loadingReceivedInvitations &&
                              !loadingSentInvitations &&
                              !loadingLinkClicks &&
                              !loadingDownloads && (
                                 <Paper
                                    sx={{
                                       p: { xs: 2, sm: 3 },
                                       textAlign: 'center',
                                       backgroundColor: theme.palette.background.paper,
                                       border: `1px dashed ${theme.palette.divider}`,
                                       borderRadius: 2,
                                       boxShadow: theme.shadows[0],
                                    }}
                                 >
                                    <PhotoLibraryIcon
                                       sx={{
                                          fontSize: { xs: 32, sm: 40 },
                                          color: theme.palette.text.secondary,
                                          mb: 1.5,
                                       }}
                                    />
                                    <Typography
                                       variant='subtitle1'
                                       color='text.secondary'
                                       sx={{
                                          fontWeight: 500,
                                          fontSize: { xs: '0.85rem', sm: '0.95rem' },
                                       }}
                                       gutterBottom
                                    >
                                       No feed found on your page
                                    </Typography>
                                    <Typography
                                       variant='body2'
                                       color='text.secondary'
                                       sx={{ fontSize: { xs: '0.8rem', sm: '0.85rem' } }}
                                    >
                                       Feed cards will appear here.
                                    </Typography>
                                 </Paper>
                              )}
                        </Stack>
                     )}
                  </Box>

                  {/* Sidebar - Right Side */}
                  <Box
                     sx={{
                        width: 440,
                        position: 'fixed',
                        right: 0,
                        top: 64,
                        bottom: 0,
                        display: { xs: 'none', lg: 'block' },
                        overflowY: 'auto',
                        zIndex: 10,
                        backgroundColor: '#0E1118', // bgSurface from theme
                        pt: 3,
                        pr: 3,
                        pl: 3,
                        borderLeft: '1px solid rgba(255, 255, 255, 0.07)', // border from theme
                        '&::-webkit-scrollbar': {
                           width: '6px',
                        },
                        '&::-webkit-scrollbar-track': {
                           background: 'transparent',
                        },
                        '&::-webkit-scrollbar-thumb': {
                           background: 'rgba(255, 255, 255, 0.07)', // border from theme
                           borderRadius: '3px',
                        },
                     }}
                  >
                     <Stack spacing={2}>
                        {/* Quick Actions Section */}
                        <Card
                           sx={{
                              borderRadius: 3,
                              boxShadow: 'none',
                              border: '1px solid rgba(255, 255, 255, 0.07)', // border from theme
                              backgroundColor: '#141820', // bgElevated from theme
                           }}
                        >
                           <CardContent sx={{ p: 2.5 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                 <AddIcon
                                    sx={{ fontSize: 20, color: 'rgba(255, 255, 255, 0.60)' }}
                                 />
                                 <Typography
                                    variant='h6'
                                    sx={{
                                       fontFamily: '"DM Sans", sans-serif',
                                       fontWeight: 600,
                                       color: theme.palette.text.primary,
                                       fontSize: '1rem',
                                    }}
                                 >
                                    Quick Actions
                                 </Typography>
                              </Box>
                              <Stack spacing={1.5}>
                                 {quickActions.map((action) => {
                                    const IconComponent = action.icon;
                                    return (
                                       <Box
                                          key={action.id}
                                          sx={{
                                             display: 'flex',
                                             alignItems: 'center',
                                             gap: 1.5,
                                             p: 1.5,
                                             borderRadius: 2,
                                             border: `1.5px solid ${action.borderColor}`,
                                             backgroundColor: '#0E1118', // bgSurface from theme
                                             cursor: 'pointer',
                                             transition: 'all 0.2s ease',
                                             '&:hover': {
                                                transform: 'translateY(-1px)',
                                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.4)',
                                                backgroundColor: '#141820', // bgElevated from theme
                                             },
                                          }}
                                          onClick={async () => {
                                             let allowed = false;
                                             if (action.id === 'event') {
                                                allowed = await canCreateEvent();
                                                if (allowed) {
                                                   setEventDialogOpen(true);
                                                }
                                             } else if (action.id === 'album') {
                                                allowed = await canCreateAlbum();
                                                if (allowed) {
                                                   setAlbumDialogOpen(true);
                                                }
                                             }
                                             if (!allowed) {
                                                setSubscriptionModalOpen(true);
                                             }
                                          }}
                                       >
                                          <Box
                                             sx={{
                                                width: 36,
                                                height: 36,
                                                borderRadius: 1.5,
                                                backgroundColor: action.iconBg,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                flexShrink: 0,
                                             }}
                                          >
                                             <IconComponent
                                                sx={{ fontSize: 18, color: action.iconColor }}
                                             />
                                          </Box>
                                          <Box sx={{ flex: 1, minWidth: 0 }}>
                                             <Typography
                                                variant='subtitle2'
                                                sx={{
                                                   fontWeight: 600,
                                                   color: theme.palette.text.primary,
                                                   fontSize: '0.85rem',
                                                   lineHeight: 1.3,
                                                }}
                                             >
                                                {action.title}
                                             </Typography>
                                             <Typography
                                                variant='caption'
                                                color='text.secondary'
                                                sx={{ fontSize: '0.7rem', lineHeight: 1.2 }}
                                             >
                                                {action.subtitle}
                                             </Typography>
                                          </Box>
                                          <AddIcon sx={{ fontSize: 18, color: action.iconColor }} />
                                       </Box>
                                    );
                                 })}
                              </Stack>
                           </CardContent>
                        </Card>
                        {/* Quick Stats section hidden */}
                        {/* <Card
                           sx={{
                              borderRadius: 3,
                              boxShadow: '0 1px 2px rgba(0, 0, 0, 0.05)',
                              backgroundColor: '#ffffff',
                           }}
                        >
                           <CardContent sx={{ p: 3 }}>
                              <Typography
                                 variant='h6'
                                 sx={{ fontWeight: 600, mb: 2, color: '#1e293b' }}
                              >
                                 Quick Stats
                              </Typography>
                              {loadingQuickStats ? (
                                 <Box sx={{ display: 'flex', justifyContent: 'space-around' }}>
                                    {[1, 2, 3].map((i) => (
                                       <Box key={i} sx={{ textAlign: 'center' }}>
                                          <Skeleton
                                             variant='text'
                                             width={50}
                                             height={40}
                                             sx={{ mx: 'auto' }}
                                          />
                                          <Skeleton
                                             variant='text'
                                             width={60}
                                             height={20}
                                             sx={{ mx: 'auto' }}
                                          />
                                       </Box>
                                    ))}
                                 </Box>
                              ) : (
                                 <Box sx={{ display: 'flex', justifyContent: 'space-around' }}>
                                    <Box sx={{ textAlign: 'center' }}>
                                       <Typography
                                          variant='h4'
                                          sx={{ fontWeight: 700, color: '#10b981', mb: 0.5 }}
                                       >
                                          {quickStats.eventsCount}
                                       </Typography>
                                       <Typography variant='body2' color='text.secondary'>
                                          Events
                                       </Typography>
                                    </Box>
                                    <Box sx={{ textAlign: 'center' }}>
                                       <Typography
                                          variant='h4'
                                          sx={{ fontWeight: 700, color: '#3b82f6', mb: 0.5 }}
                                       >
                                          {quickStats.totalPhotos}
                                       </Typography>
                                       <Typography variant='body2' color='text.secondary'>
                                          Photos
                                       </Typography>
                                    </Box>
                                    <Box sx={{ textAlign: 'center' }}>
                                       <Typography
                                          variant='h4'
                                          sx={{ fontWeight: 700, color: '#f59e0b', mb: 0.5 }}
                                       >
                                          {quickStats.albumInvites}
                                       </Typography>
                                       <Typography variant='body2' color='text.secondary'>
                                          Invites
                                       </Typography>
                                    </Box>
                                 </Box>
                              )}
                           </CardContent>
                        </Card> */}
                        <Card
                           sx={{
                              borderRadius: 3,
                              boxShadow: 'none',
                              border: '1px solid rgba(255, 255, 255, 0.07)', // border from theme
                              backgroundColor: '#141820', // bgElevated from theme
                           }}
                        >
                           <CardContent sx={{ p: 3 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                                 <HistoryIcon
                                    sx={{ fontSize: 20, color: theme.palette.text.secondary }}
                                 />
                                 <Typography
                                    variant='h6'
                                    sx={{
                                       fontFamily: '"DM Sans", sans-serif',
                                       fontWeight: 600,
                                       color: theme.palette.text.primary,
                                       flex: 1,
                                    }}
                                 >
                                    Album Activity
                                 </Typography>
                                 <IconButton
                                    size='small'
                                    onClick={() => fetchActivities()}
                                    disabled={loadingRecentActivities}
                                    aria-label='Refresh album activity'
                                    sx={{ color: theme.palette.text.secondary }}
                                 >
                                    <RefreshIcon fontSize='small' />
                                 </IconButton>
                              </Box>
                              {loadingRecentActivities ? (
                                 <Stack spacing={1.5}>
                                    {[1, 2, 3].map((i) => (
                                       <Box
                                          key={i}
                                          sx={{ display: 'flex', gap: 1, alignItems: 'center' }}
                                       >
                                          <Skeleton variant='circular' width={32} height={32} />
                                          <Box sx={{ flex: 1 }}>
                                             <Skeleton variant='text' width='80%' height={18} />
                                             <Skeleton variant='text' width='40%' height={14} />
                                          </Box>
                                       </Box>
                                    ))}
                                 </Stack>
                              ) : recentActivities.length === 0 ? (
                                 <Typography
                                    variant='body2'
                                    color='text.secondary'
                                    sx={{ textAlign: 'center', py: 2 }}
                                 >
                                    No recent album activity
                                 </Typography>
                              ) : (
                                 <Stack spacing={1.5}>
                                    {recentActivities.map((activity) => {
                                       const actorProfile = activity.resolvedActorId
                                          ? activityUsers[activity.resolvedActorId]
                                          : null;
                                       const isActor =
                                          activity.resolvedActorId === currentUser?.uid;
                                       const isOwner = activity.userId === currentUser?.uid;

                                       // Determine which profile to display and the message
                                       let displayProfile = actorProfile;
                                       let displayMessage = activity.details || activity.action;

                                       if (activity.action === 'access_request') {
                                          // Show requester's avatar
                                          displayProfile = actorProfile;
                                          displayMessage = isActor
                                             ? `You requested access to ${activity.albumTitle || 'album'}`
                                             : `${actorProfile?.displayName || 'Someone'} requested access`;
                                       } else if (
                                          activity.action === 'approved_request' ||
                                          activity.action === 'denied_request'
                                       ) {
                                          // Show the person who was approved/denied (target user)
                                          if (
                                             activity.targetUserDisplayName &&
                                             activity.targetUserPhotoURL !== undefined
                                          ) {
                                             displayProfile = {
                                                displayName: activity.targetUserDisplayName,
                                                photoURL: activity.targetUserPhotoURL,
                                             };
                                          }

                                          if (activity.action === 'approved_request') {
                                             displayMessage = isActor
                                                ? `You approved ${activity.targetUserDisplayName || 'access'} to ${activity.albumTitle || 'album'}`
                                                : `Your access to ${activity.albumTitle || 'album'} was approved`;
                                          } else {
                                             displayMessage = isActor
                                                ? `You denied ${activity.targetUserDisplayName || 'access'} to ${activity.albumTitle || 'album'}`
                                                : `Your access to ${activity.albumTitle || 'album'} was denied`;
                                          }
                                       } else if (activity.action === 'cancel_invite') {
                                          displayMessage = 'Cancelled album invite';
                                       } else if (activity.action === 'resend_invite') {
                                          displayMessage = 'Resent album invites';
                                       } else if (activity.action === 'view_album') {
                                          displayMessage = 'Viewed album';
                                       } else if (activity.action === 'photo_download') {
                                          displayMessage = isActor
                                             ? `You downloaded a photo from ${activity.albumTitle || 'album'}`
                                             : `${actorProfile?.displayName || 'Someone'} downloaded a photo`;
                                       } else if (activity.action === 'album_invitation_received') {
                                          displayMessage = isOwner
                                             ? `Received album invitation from ${actorProfile?.displayName || 'Someone'}`
                                             : activity.details || 'Received album invitation';
                                       } else if (activity.action === 'album_created') {
                                          displayMessage = `Created album: ${activity.albumTitle || 'Untitled'}`;
                                       } else if (
                                          activity.action === 'album_invitation_accepted' ||
                                          activity.action === 'album_invite_accepted'
                                       ) {
                                          // Same family as album_invitation_received; legacy: album_invite_accepted
                                          displayMessage =
                                             activity.details ||
                                             (isActor || isOwner
                                                ? `You accepted an invitation to ${activity.albumTitle || 'this album'}`
                                                : `Invitation accepted for ${activity.albumTitle || 'album'}`);
                                       } else if (activity.action === 'album_access_granted') {
                                          displayMessage =
                                             activity.details ||
                                             `You now have access to ${activity.albumTitle || 'this album'}`;
                                       } else if (activity.action === 'event_created') {
                                          displayMessage = `Created event: ${activity.details?.replace('Created event: ', '') || 'Untitled'}`;
                                       }

                                       const hasAlbumId = !!activity.albumId;
                                       const coverUrl = activity.albumId
                                          ? albumIdToCover[activity.albumId]
                                          : undefined;
                                       const userPopupActions = [
                                          'access_request',
                                          'view_album',
                                          'approved_request',
                                          'denied_request',
                                       ];
                                       const hasPopup =
                                          userPopupActions.includes(activity.action) ||
                                          (!userPopupActions.includes(activity.action) &&
                                             (!!activity.albumId || !!activity.albumTitle));

                                       const timeStr = activity.createdAt?.toDate
                                          ? formatDistanceToNow(activity.createdAt.toDate(), {
                                               addSuffix: true,
                                            })
                                          : 'Just now';
                                       const tooltipTitle = hasPopup
                                          ? `${activity.albumTitle || 'Album'} · ${displayMessage} · ${timeStr}`
                                          : '';

                                       const rowBox = (
                                          <Box
                                             key={activity.id}
                                             onClick={
                                                hasPopup
                                                   ? () => handleActivityRowClick(activity)
                                                   : undefined
                                             }
                                             role={hasPopup ? 'button' : undefined}
                                             sx={{
                                                display: 'flex',
                                                gap: 1,
                                                alignItems: 'flex-start',
                                                p: 1,
                                                borderRadius: 2,
                                                border: '1px solid rgba(255, 255, 255, 0.07)', // border from theme
                                                backgroundColor: '#0E1118', // bgSurface from theme
                                                ...(hasPopup && {
                                                   cursor: 'pointer',
                                                   '&:hover': {
                                                      backgroundColor: 'rgba(255, 255, 255, 0.05)', // hover from theme
                                                   },
                                                }),
                                             }}
                                          >
                                             {hasAlbumId && (
                                                <Box
                                                   sx={{
                                                      width: 40,
                                                      height: 40,
                                                      flexShrink: 0,
                                                      borderRadius: 1.5,
                                                      overflow: 'hidden',
                                                      backgroundColor: 'rgba(255, 255, 255, 0.07)', // border from theme
                                                      display: 'flex',
                                                      alignItems: 'center',
                                                      justifyContent: 'center',
                                                   }}
                                                >
                                                   {coverUrl ? (
                                                      <Box
                                                         component='img'
                                                         src={coverUrl}
                                                         alt=''
                                                         sx={{
                                                            width: '100%',
                                                            height: '100%',
                                                            objectFit: 'cover',
                                                         }}
                                                      />
                                                   ) : (
                                                      <PhotoAlbumIcon
                                                         sx={{
                                                            fontSize: 20,
                                                            color: theme.palette.text.secondary,
                                                         }}
                                                      />
                                                   )}
                                                </Box>
                                             )}
                                             {displayProfile && (
                                                <Avatar
                                                   src={displayProfile.photoURL}
                                                   alt={displayProfile.displayName}
                                                   sx={{
                                                      width: 32,
                                                      height: 32,
                                                      flexShrink: 0,
                                                      fontSize: '0.75rem',
                                                      backgroundColor: 'rgba(255, 255, 255, 0.07)', // border from theme
                                                      color: theme.palette.text.primary,
                                                      fontWeight: 600,
                                                   }}
                                                >
                                                   {displayProfile.displayName?.[0]?.toUpperCase()}
                                                </Avatar>
                                             )}
                                             <Box
                                                sx={{
                                                   flex: 1,
                                                   minWidth: 0,
                                                   display: 'flex',
                                                   flexDirection: 'column',
                                                   gap: 0.5,
                                                }}
                                             >
                                                <Typography
                                                   variant='subtitle2'
                                                   sx={{
                                                      color: '#FFFFFF',
                                                      fontWeight: 600,
                                                      overflow: 'hidden',
                                                      textOverflow: 'ellipsis',
                                                      display: '-webkit-box',
                                                      WebkitLineClamp: 2,
                                                      WebkitBoxOrient: 'vertical',
                                                      wordBreak: 'break-word',
                                                   }}
                                                >
                                                   {activity.albumTitle || 'Album'}
                                                </Typography>
                                                <Typography
                                                   variant='body2'
                                                   sx={{
                                                      color: 'rgba(255, 255, 255, 0.60)',
                                                      overflow: 'hidden',
                                                      textOverflow: 'ellipsis',
                                                      display: '-webkit-box',
                                                      WebkitLineClamp: 3,
                                                      WebkitBoxOrient: 'vertical',
                                                      wordBreak: 'break-word',
                                                   }}
                                                >
                                                   {displayMessage}
                                                </Typography>
                                                <Typography
                                                   variant='caption'
                                                   sx={{
                                                      color: 'rgba(255, 255, 255, 0.45)',
                                                      mt: 0.25,
                                                   }}
                                                >
                                                   {activity.createdAt?.toDate
                                                      ? formatDistanceToNow(
                                                           activity.createdAt.toDate(),
                                                           { addSuffix: true },
                                                        )
                                                      : 'Just now'}
                                                </Typography>
                                             </Box>
                                          </Box>
                                       );
                                       return hasPopup ? (
                                          <Tooltip
                                             key={activity.id}
                                             title={tooltipTitle}
                                             enterDelay={400}
                                             placement='left'
                                          >
                                             <span style={{ display: 'block' }}>{rowBox}</span>
                                          </Tooltip>
                                       ) : (
                                          rowBox
                                       );
                                    })}
                                 </Stack>
                              )}
                           </CardContent>
                        </Card>
                     </Stack>
                  </Box>
                  {/* End Sidebar */}
               </Box>
            </Box>
         </Box>

         {/* Snackbar for notifications */}
         <EventCreationDialog
            open={eventDialogOpen}
            onClose={() => setEventDialogOpen(false)}
            onSuccess={handleEventCreated}
         />
         <CreateAlbumDialog
            open={albumDialogOpen}
            onClose={() => setAlbumDialogOpen(false)}
            onCreateAlbum={handleCreateAlbum}
         />
         {currentUser && (
            <EditProfileDialog
               open={editProfileDialogOpen}
               onClose={handleCloseEditProfile}
               userId={currentUser.uid}
               profileData={profileData}
               onProfileUpdated={handleProfileUpdated}
            />
         )}
         {activityPopupType === 'user' && (
            <>
               {loadingActivityUser ? (
                  <Dialog open onClose={handleCloseActivityPopup} maxWidth='xs' fullWidth>
                     <DialogContent sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                        <CircularProgress />
                     </DialogContent>
                  </Dialog>
               ) : (
                  <ParticipantProfileModal
                     open
                     onClose={handleCloseActivityPopup}
                     participant={selectedUserForPopup}
                     currentUserId={currentUser?.uid}
                  />
               )}
            </>
         )}
         {activityPopupType === 'album' &&
            selectedActivity &&
            (() => {
               const album = selectedActivity.albumId
                  ? accessibleAlbums.find((a) => a.id === selectedActivity.albumId)
                  : null;
               const coverUrl = selectedActivity.albumId
                  ? (albumIdToCover[selectedActivity.albumId] ?? album?.coverPhotoUrl)
                  : undefined;
               const title = album?.title ?? selectedActivity.albumTitle ?? 'Album';
               return (
                  <Dialog open onClose={handleCloseActivityPopup} maxWidth='sm' fullWidth>
                     <DialogTitle>Album details</DialogTitle>
                     <DialogContent>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                           <Box
                              sx={{
                                 display: 'flex',
                                 alignItems: 'center',
                                 gap: 2,
                                 flexWrap: 'wrap',
                              }}
                           >
                              <Box
                                 sx={{
                                    width: 80,
                                    height: 80,
                                    borderRadius: 2,
                                    overflow: 'hidden',
                                    backgroundColor: theme.palette.background.paper,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                 }}
                              >
                                 {coverUrl ? (
                                    <Box
                                       component='img'
                                       src={coverUrl}
                                       alt=''
                                       sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                                    />
                                 ) : (
                                    <PhotoAlbumIcon
                                       sx={{ fontSize: 40, color: theme.palette.text.secondary }}
                                    />
                                 )}
                              </Box>
                              <Box>
                                 <Typography variant='h6' sx={{ fontWeight: 600 }}>
                                    {title}
                                 </Typography>
                                 {album && (
                                    <>
                                       <Typography variant='body2' color='text.secondary'>
                                          {album.photoCount} photo
                                          {album.photoCount !== 1 ? 's' : ''}
                                       </Typography>
                                       {album.ownerName && (
                                          <Typography variant='body2' color='text.secondary'>
                                             Owner: {album.ownerName}
                                          </Typography>
                                       )}
                                       {album.eventTitle && (
                                          <Typography variant='body2' color='text.secondary'>
                                             Event: {album.eventTitle}
                                             {album.eventDate && ` · ${album.eventDate}`}
                                          </Typography>
                                       )}
                                    </>
                                 )}
                                 {!album && (
                                    <Typography variant='body2' color='text.secondary'>
                                       Details limited. You may not have access to this album.
                                    </Typography>
                                 )}
                              </Box>
                           </Box>
                        </Box>
                     </DialogContent>
                     <DialogActions>
                        <Button onClick={handleCloseActivityPopup}>Close</Button>
                     </DialogActions>
                  </Dialog>
               );
            })()}
         <Dialog
            open={photographerSignupDialogOpen}
            onClose={() => {
               if (!photographerSignupSubmitting) {
                  setPhotographerSignupDialogOpen(false);
                  setPhotographerSignupDialogSuccess(false);
               }
            }}
            maxWidth='sm'
            fullWidth
            PaperProps={{ sx: { borderRadius: 2 } }}
         >
            <DialogTitle
               sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  pr: 1,
               }}
            >
               {photographerSignupDialogSuccess ? 'Welcome!' : 'Add photographer to your account'}
               <IconButton
                  size='small'
                  onClick={() => {
                     if (!photographerSignupSubmitting) {
                        setPhotographerSignupDialogOpen(false);
                        setPhotographerSignupDialogSuccess(false);
                     }
                  }}
                  disabled={photographerSignupSubmitting}
                  aria-label='Close'
               >
                  <CloseIcon />
               </IconButton>
            </DialogTitle>
            <DialogContent>
               {photographerSignupDialogSuccess ? (
                  <Box sx={{ textAlign: 'center', py: 1 }}>
                     <Typography variant='h6' sx={{ fontWeight: 700, mb: 1 }}>
                        You&apos;re a photographer on WeTrippen
                     </Typography>
                     <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
                        Your 30-day trial is active. Open MyKiosk to manage events, albums, and
                        delivery.
                     </Typography>
                     <Button
                        variant='contained'
                        size='large'
                        endIcon={<OpenInNewIcon />}
                        disabled={!photographerWebsite}
                        onClick={() => handleOpenMyKioskFromPromo(photographerWebsite)}
                     >
                        Open MyKiosk dashboard
                     </Button>
                  </Box>
               ) : (
                  <RegisterPhotographerForm
                     active={photographerSignupDialogOpen && !photographerSignupDialogSuccess}
                     defaultDisplayName={
                        currentUser?.displayName || currentUser?.email?.split('@')[0] || ''
                     }
                     onSubmittingChange={setPhotographerSignupSubmitting}
                     onSuccess={() => {
                        setPhotographerSignupDialogSuccess(true);
                        handleFeedPhotographerRegistered();
                     }}
                  />
               )}
            </DialogContent>
            {photographerSignupDialogSuccess && (
               <DialogActions sx={{ px: 3, pb: 2 }}>
                  <Button
                     onClick={() => {
                        setPhotographerSignupDialogOpen(false);
                        setPhotographerSignupDialogSuccess(false);
                     }}
                  >
                     Close
                  </Button>
               </DialogActions>
            )}
         </Dialog>

         <Snackbar
            open={snackbarOpen}
            autoHideDuration={6000}
            onClose={() => setSnackbarOpen(false)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
         >
            <Alert
               onClose={() => setSnackbarOpen(false)}
               severity={snackbarSeverity}
               sx={{ width: '100%' }}
            >
               {snackbarMessage}
            </Alert>
         </Snackbar>
      </SubscriptionGuard>
   );
};

export default Feed;
